import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRegisterAdminRole, getRegisterAdminToken, getRegisterAdminEmail } from "./auth.ts";
import { api, apiWithRetry } from "./api.ts";
import { TicketDetailPanel } from "./components/TicketDetailPanel.tsx";
import { StatusChangeModal } from "./components/StatusChangeModal.tsx";
import { FilterBar } from "./components/FilterBar.tsx";
import { SortBar } from "./components/SortBar.tsx";
import { TicketCard } from "./components/TicketCard.tsx";
import { MyTasksModal } from "./components/MyTasksModal.tsx";
import { MergeSuggestBanner, fetchMergeSuggestions } from "./components/MergeSuggestBanner.tsx";
import type { TicketSortKey, TicketSortOrder } from "./constants.ts";
import {
  MANUAL_CHANNELS,
  STATUS_LABEL,
  CATEGORY_LABEL,
  DEFAULT_CHANNEL_FILTER,
  DEFAULT_STATUS_FILTER,
  DEFAULT_SORT_KEY,
  DEFAULT_SORT_ORDER,
} from "./constants.ts";
import { ticketDisplayTitle, ticketContactEmails } from "./utils/ticketDisplay.ts";
import { canEditTicketChannel, channelSelectOptions } from "./utils/ticketChannelEdit.ts";
import type {
  Channel,
  MergeResponse,
  MergeUndoResponse,
  Ticket,
  Thread,
  Message,
  EmailLog,
  NotifyTemplate,
  SendPreview,
  MailPreview,
  MailTemplate,
  MailSettings,
  AgentProfile,
  PartMaster,
  ScheduleSaveResult,
  MergeSuggestionCandidate,
  SupportTask,
} from "./types.ts";
import type { MentionUser } from "./utils/messageDisplay.ts";
import { formatAttachmentForMessage, uploadTicketAttachments } from "./utils/attachments.ts";
import { openDashMac } from "./utils/dashLinks.ts";
import { appendMessageUnique, applyThreadMessageToTimeline, type HasThreadMessage } from "./utils/threadMessage.ts";
import { hasSalutation, setSalutation } from "./utils/mailBody.ts";
import { slaState, slaSortValue } from "./utils/sla.ts";
import { buildThreadByTicket, pickTicketThread } from "./utils/threadPick.ts";
import { useMinuteTick } from "./utils/useMinuteTick.ts";
import { useUiState, readUiState, writeUiState } from "./utils/uiState.ts";

function ContactListInput({
  label, values, onChange, placeholder, type = "text", required = false, note,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  type?: string;
  required?: boolean;
  note?: string;
}) {
  const rows = values.length ? values : [""];
  const set = (i: number, v: string) => onChange(rows.map((x, j) => (j === i ? v : x)));
  const add = () => onChange([...rows, ""]);
  const del = (i: number) => {
    const next = rows.filter((_, j) => j !== i);
    onChange(next.length ? next : [""]);
  };
  const inp: React.CSSProperties = {
    padding: "0.5rem 0.7rem", borderRadius: "6px", border: "1.5px solid var(--border,#e0e4e0)",
    fontFamily: "inherit", width: "100%", boxSizing: "border-box",
  };
  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-secondary,#5f635f)", marginBottom: "0.2rem" }}>
        {label}{required ? <span style={{ color: "var(--danger,#e53935)" }}> *必須</span> : <span style={{ fontWeight: 400 }}>（任意）</span>}
        {note ? <span style={{ fontWeight: 400 }}> — {note}</span> : null}
      </div>
      {rows.map((v, i) => (
        <div key={i} style={{ display: "flex", gap: "0.35rem", marginBottom: "0.25rem" }}>
          <input
            style={inp} type={type} placeholder={placeholder} value={v}
            onChange={(e) => set(i, e.target.value)}
          />
          <button
            type="button" onClick={() => del(i)} title="この行を削除"
            style={{ padding: "0 0.6rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "var(--surface,#fff)", color: "var(--text-secondary,#5f635f)", cursor: "pointer" }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button" onClick={add}
        style={{ padding: "0.2rem 0.6rem", borderRadius: 5, border: "1px dashed var(--primary)", background: "transparent", color: "var(--primary)", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}
      >
        ＋ {label}を追加
      </button>
    </div>
  );
}

// 顧客メールのテンプレ・署名設定（Phase 2）。
// 旧実装はステータス遷移の4件だけを編集でき、受付確認・訂正の文面はコード定数で運用者が触れなかった。
// 署名 footer も固定文だったため、組織名の変更や担当者名の表示を運用側で選べなかった。
function TemplateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [settings, setSettings] = useState<MailSettings | null>(null);
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  const [draft, setDraft] = useState<Record<string, { subject: string; body: string }>>({});
  const [settingsDraft, setSettingsDraft] = useState<MailSettings | null>(null);
  const [newProfile, setNewProfile] = useState({ email: "", display_name: "" });
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api<{ templates: MailTemplate[]; placeholders: string[] }>(`/support/mail-templates`),
        api<{ settings: MailSettings; agent_profiles: AgentProfile[] }>(`/support/mail-settings`),
      ]);
      setTemplates(t.templates);
      setPlaceholders(t.placeholders);
      setSettings(s.settings);
      setSettingsDraft(s.settings);
      setProfiles(s.agent_profiles);
      setDraft({});
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const get = (t: MailTemplate) => draft[t.key] ?? { subject: t.subject_suffix, body: t.body };

  const save = async (t: MailTemplate) => {
    const d = get(t);
    try {
      await api(`/support/mail-templates/${t.key}`, {
        method: "PUT",
        body: JSON.stringify({ subject_suffix: d.subject.trim(), body: d.body }),
      });
      await reload();
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const reset = async (t: MailTemplate) => {
    try {
      await api(`/support/mail-templates/${t.key}`, { method: "DELETE" });
      await reload();
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const saveSettings = async () => {
    if (!settingsDraft) return;
    try {
      await api(`/support/mail-settings`, { method: "PUT", body: JSON.stringify(settingsDraft) });
      await reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const addProfile = async () => {
    try {
      await api(`/support/agent-profiles`, { method: "PUT", body: JSON.stringify(newProfile) });
      setNewProfile({ email: "", display_name: "" });
      await reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const settingsDirty = !!settings && !!settingsDraft && JSON.stringify(settings) !== JSON.stringify(settingsDraft);
  const inputStyle = { width: "100%", padding: "0.4rem 0.6rem", border: "1.5px solid var(--border,#e0e4e0)", borderRadius: 6, fontSize: "0.85rem" } as const;
  const btnPrimary = (on: boolean) => ({ padding: "0.35rem 0.9rem", borderRadius: 6, border: "none", background: on ? "var(--primary)" : "var(--border,#e0e4e0)", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: on ? "pointer" : "default" } as const);
  const btnOutline = { padding: "0.35rem 0.9rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", background: "var(--surface,#fff)", color: "var(--text-secondary,#5f635f)", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" } as const;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "4vh 1rem", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface,#fff)", borderRadius: 12, maxWidth: 760, width: "100%", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <h3 style={{ margin: 0, fontWeight: 800 }}>✉️ 顧客メールのテンプレートと署名</h3>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: "1.2rem", cursor: "pointer", color: "var(--text-secondary,#5f635f)" }}>✕</button>
        </div>
        {err && (
          <div style={{ fontSize: "0.78rem", color: "var(--danger-ink)", background: "var(--danger-light)", borderRadius: 6, padding: "0.5rem 0.7rem", marginBottom: "0.7rem" }}>{err}</div>
        )}
        <div style={{ fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", marginBottom: "1rem", lineHeight: 1.8 }}>
          件名は「[受付番号] 」の後ろに付きます。本文の後には下の「署名」が自動付与されます。
          <br />
          <strong>宛名は「宛名を入れる」で選びます</strong>（本文先頭の <code>{"{{customer_salutation}}"}</code> が付け外しされ、送信時に「〇〇 様」／連絡担当者名が空なら「ご担当者様」に置き換わります）。
          {placeholders.length > 0 && (
            <>
              <br />使えるプレースホルダ: {placeholders.map((p) => <code key={p} style={{ marginRight: "0.4rem" }}>{`{{${p}}}`}</code>)}
            </>
          )}
        </div>

        {/* 署名 footer 設定 */}
        {settingsDraft && (
          <div style={{ border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem", marginBottom: "1.1rem", background: "var(--bg,#f9fbf9)" }}>
            <div style={{ fontWeight: 800, marginBottom: "0.5rem" }}>署名（全ての顧客メール共通）</div>
            <div style={{ display: "grid", gap: "0.45rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>組織名</label>
              <input value={settingsDraft.org_name} onChange={(e) => setSettingsDraft({ ...settingsDraft, org_name: e.target.value })} style={inputStyle} />
              <label style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>返信のご案内</label>
              <input value={settingsDraft.reply_note} onChange={(e) => setSettingsDraft({ ...settingsDraft, reply_note: e.target.value })} style={inputStyle} />
              <label style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>照会URLの案内（照会URLが無いチケットでは出ません）</label>
              <input value={settingsDraft.portal_note} onChange={(e) => setSettingsDraft({ ...settingsDraft, portal_note: e.target.value })} style={inputStyle} />
              <label style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>追加の行（任意・受付時間など）</label>
              <input value={settingsDraft.extra_lines ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, extra_lines: e.target.value })} style={inputStyle} />
              <label style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontSize: "0.82rem", marginTop: "0.2rem" }}>
                <input type="checkbox" checked={settingsDraft.include_assignee} onChange={(e) => setSettingsDraft({ ...settingsDraft, include_assignee: e.target.checked })} />
                担当者名を署名に出す
              </label>
              <div style={{ fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)" }}>
                ⓘ 下に表示名が登録されている担当者のみ「担当: 〇〇」が出ます。未登録の担当者は出しません（メールアドレスを顧客に見せないため）。
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
              <button disabled={!settingsDirty} onClick={() => void saveSettings()} style={btnPrimary(settingsDirty)}>署名を保存</button>
              {settingsDirty && <button onClick={() => setSettingsDraft(settings)} style={btnOutline}>変更を取り消す</button>}
            </div>

            <div style={{ marginTop: "0.9rem", borderTop: "1px solid var(--border,#e0e4e0)", paddingTop: "0.7rem" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, marginBottom: "0.4rem" }}>担当者の表示名</div>
              {profiles.length === 0 ? (
                <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.4rem" }}>まだ登録がありません。</div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
                  {profiles.map((p) => (
                    <span key={p.email} style={{ fontSize: "0.74rem", background: "var(--surface,#fff)", border: "1px solid var(--border,#e0e4e0)", borderRadius: 999, padding: "0.15rem 0.6rem" }}>
                      <span className="mono">{p.email}</span> → <strong>{p.display_name}</strong>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <input value={newProfile.email} onChange={(e) => setNewProfile({ ...newProfile, email: e.target.value })} placeholder="tanto@naturenix.co.jp" style={{ ...inputStyle, flex: 2 }} />
                <input value={newProfile.display_name} onChange={(e) => setNewProfile({ ...newProfile, display_name: e.target.value })} placeholder="表示名（例: 赤熊）" style={{ ...inputStyle, flex: 1 }} />
                <button
                  disabled={!newProfile.email.trim() || !newProfile.display_name.trim()}
                  onClick={() => void addProfile()}
                  style={btnPrimary(!!newProfile.email.trim() && !!newProfile.display_name.trim())}
                >
                  登録
                </button>
              </div>
            </div>
          </div>
        )}

        {/* テンプレ一覧 */}
        {templates.map((t) => {
          const d = get(t);
          const dirty = d.subject !== t.subject_suffix || d.body !== t.body;
          // 本文は「コード既定が空の kind（訂正＝毎回オペレータが書く）」だけ空で保存できる（BE と同条件）
          const bodyOk = !!d.body.trim() || !t.default_body.trim();
          const canSave = dirty && !!d.subject.trim() && bodyOk;
          return (
            <div key={t.key} style={{ border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem", marginBottom: "0.75rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                <span style={{ fontWeight: 800 }}>{t.label}</span>
                <span className="mono" style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)" }}>{t.key}</span>
                {t.is_custom ? (
                  <span style={{ fontSize: "0.68rem", color: "var(--primary)", fontWeight: 700 }}>カスタム</span>
                ) : (
                  <span style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)" }}>既定</span>
                )}
                {/* 送信画面と同じ操作（本文先頭の {{customer_salutation}} の付け外し）に揃える */}
                <label
                  style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", cursor: "pointer" }}
                  title="本文の先頭に「〇〇 様」を入れます（連絡担当者名が空なら「ご担当者様」）"
                >
                  <input
                    type="checkbox"
                    checked={hasSalutation(d.body)}
                    onChange={(e) => setDraft((p) => ({ ...p, [t.key]: { subject: d.subject, body: setSalutation(d.body, e.target.checked) } }))}
                  />
                  宛名を入れる
                </label>
              </div>
              <input
                value={d.subject}
                onChange={(e) => setDraft((p) => ({ ...p, [t.key]: { subject: e.target.value, body: d.body } }))}
                placeholder="件名接尾"
                style={{ ...inputStyle, marginBottom: "0.4rem" }}
              />
              <textarea
                value={d.body}
                onChange={(e) => setDraft((p) => ({ ...p, [t.key]: { subject: d.subject, body: e.target.value } }))}
                rows={4}
                placeholder="本文"
                style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.4rem" }}>
                <button disabled={!canSave} onClick={() => void save(t)} style={btnPrimary(canSave)}>
                  保存
                </button>
                {t.is_custom && <button onClick={() => void reset(t)} style={btnOutline}>既定に戻す</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 社内トリアージ: メール起票は機体情報・分類が空で入るため、担当者が後から補完・訂正する
const TRIAGE_FIELDS: { key: string; label: string; placeholder?: string; required?: boolean }[] = [
  { key: "title", label: "件名", placeholder: "例: 岡野歯科医院 — 充電不可", required: true },
  { key: "category", label: "種別", placeholder: "設置トラブル / 緊急トラブル対応 / 後日対応 / usage / repair …" },
  { key: "unit_serial", label: "シリアル", placeholder: "NXEC0700J*000127" },
  { key: "device_mac", label: "MAC", placeholder: "D0:CF:13:40:A5:0C" },
  { key: "product", label: "製品", placeholder: "EC-700" },
  { key: "company", label: "会社名" },
  { key: "contact_name", label: "担当者名" },
  { key: "contact_email", label: "連絡先メール" },
  { key: "callback_contact", label: "折り返し連絡先", placeholder: "電話番号など" },
  { key: "install_date", label: "設置日" },
];

function TriageModal({ ticket, onClose, onSave, canEditChannel }: {
  ticket: Ticket;
  onClose: () => void;
  onSave: (t: Ticket, fields: Record<string, string>, channel?: Channel) => void;
  canEditChannel: boolean;
}) {
  const [d, setD] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    for (const f of TRIAGE_FIELDS) o[f.key] = ((ticket as unknown as Record<string, unknown>)[f.key] as string) || "";
    return o;
  });
  const [channel, setChannel] = useState<Channel>(ticket.channel);
  const dirtyFields = TRIAGE_FIELDS.some((f) => (d[f.key] || "") !== (((ticket as unknown as Record<string, unknown>)[f.key] as string) || ""));
  const dirtyChannel = canEditChannel && channel !== ticket.channel;
  const dirty = dirtyFields || dirtyChannel;
  const channelOptions = channelSelectOptions(ticket.channel);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 210, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "4vh 1rem", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface,#fff)", borderRadius: 12, maxWidth: 620, width: "100%", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
          <h3 style={{ margin: 0, fontWeight: 800 }}>🔧 トリアージ — {ticket.ticket_number}</h3>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: "1.2rem", cursor: "pointer", color: "var(--text-secondary,#5f635f)" }}>✕</button>
        </div>
        <div style={{ fontSize: "0.76rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.9rem" }}>
          自動起票（機体監視）の件名・区分を入電対応向けに直したり、メール起票の空フィールドを補完できます。変更は監査ログに残ります。
        </div>
        {canEditChannel && (
          <label style={{ display: "block", marginBottom: "0.55rem" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-secondary,#5f635f)" }}>区分（チャネル）</span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as Channel)}
              style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1.5px solid var(--border,#e0e4e0)", borderRadius: 6, fontSize: "0.85rem", marginTop: "0.15rem" }}
            >
              {channelOptions.map((c) => (
                <option key={c.key} value={c.key} disabled={c.key === "device_monitor"}>{c.label}</option>
              ))}
            </select>
          </label>
        )}
        {TRIAGE_FIELDS.map((f) => (
          <label key={f.key} style={{ display: "block", marginBottom: "0.55rem" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-secondary,#5f635f)" }}>{f.label}</span>
            <input
              value={d[f.key] || ""}
              placeholder={f.placeholder || ""}
              onChange={(e) => setD((p) => ({ ...p, [f.key]: e.target.value }))}
              style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1.5px solid var(--border,#e0e4e0)", borderRadius: 6, fontSize: "0.85rem", marginTop: "0.15rem" }}
            />
          </label>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.8rem" }}>
          <button onClick={onClose} style={{ padding: "0.45rem 1.1rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", background: "var(--surface,#fff)", color: "var(--text-secondary,#5f635f)", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>キャンセル</button>
          <button
            onClick={() => onSave(ticket, d, dirtyChannel ? channel : undefined)}
            disabled={!dirty}
            style={{ padding: "0.45rem 1.3rem", borderRadius: 6, border: "none", background: dirty ? "var(--primary)" : "var(--border,#e0e4e0)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: dirty ? "pointer" : "default" }}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

// 顧客メールの「実際に届く形」を BE のレンダラから取って表示する。
// 宛先はチェックで絞り込み。追加は任意アドレス可（必要ならチケット連絡先にも保存）。
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function SendPreviewModal({ preview, onClose }: { preview: SendPreview; onClose: () => void }) {
  const [body, setBody] = useState(preview.body);
  const [sending, setSending] = useState(false);
  const [rendered, setRendered] = useState<MailPreview | null>(null);
  const [renderErr, setRenderErr] = useState("");
  const [candidates, setCandidates] = useState<string[]>(() => {
    const base = preview.candidateEmails?.length
      ? preview.candidateEmails
      : (preview.to || "").split(",").map((s) => s.trim()).filter(Boolean);
    const init = preview.initialSelectedEmails || [];
    const seen = new Set(base.map((e) => e.toLowerCase()));
    const merged = [...base];
    for (const e of init) {
      const k = e.toLowerCase();
      if (k && !seen.has(k)) {
        seen.add(k);
        merged.push(e.toLowerCase());
      }
    }
    return merged;
  });
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(() => {
    const base = preview.candidateEmails?.length
      ? preview.candidateEmails
      : (preview.to || "").split(",").map((s) => s.trim()).filter(Boolean);
    const init = preview.initialSelectedEmails?.length
      ? preview.initialSelectedEmails
      : base;
    return new Set(init.map((e) => e.toLowerCase()));
  });
  const [addEmailInput, setAddEmailInput] = useState("");
  const [addErr, setAddErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [persistToTicket, setPersistToTicket] = useState(false);
  const canPreview = !!preview.ticketId && !!preview.kind;
  const selectedList = useMemo(() => Array.from(selectedEmails), [selectedEmails]);

  // 本文・宛先編集に追従して完成形を取り直す（打鍵ごとに叩かないよう 400ms デバウンス）
  useEffect(() => {
    if (!canPreview) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      apiWithRetry<MailPreview>(`/support/tickets/${preview.ticketId}/mail-preview`, {
        method: "POST",
        body: JSON.stringify({
          kind: preview.kind,
          body,
          to_addrs: selectedList,
        }),
      })
        .then((d) => { if (alive) { setRendered(d); setRenderErr(""); } })
        .catch((e) => { if (alive) setRenderErr(e instanceof Error ? e.message : String(e)); });
    }, 400);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [body, canPreview, preview.ticketId, preview.kind, selectedList]);

  // 宛名は本文先頭の {{customer_salutation}} で出す。チェックはその付け外しに落とす。
  const salutationOn = hasSalutation(body);

  const toLine = selectedList.length
    ? selectedList.join(", ")
    : (rendered ? rendered.to_addrs.join(", ") : preview.to);
  const subjectLine = rendered ? rendered.subject : preview.subject;
  // 宛先未選択時の no_selected_recipient は「送れない」警告ではなく未選択として扱う
  const blocked = selectedList.length > 0 && !!rendered?.suppressed;
  const canSend = body.trim().length > 0 && selectedList.length > 0 && !blocked && !sending;

  const toggleEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const doAddEmail = async () => {
    const raw = addEmailInput.trim().toLowerCase();
    setAddErr("");
    if (!raw || !EMAIL_RE.test(raw)) {
      setAddErr("メールアドレスの形式を確認してください");
      return;
    }
    if (candidates.some((c) => c.toLowerCase() === raw)) {
      setSelectedEmails((prev) => new Set(prev).add(raw));
      setAddEmailInput("");
      return;
    }
    if (persistToTicket && preview.onPersistEmail) {
      setAdding(true);
      try {
        await preview.onPersistEmail(raw);
      } catch (e) {
        setAddErr(e instanceof Error ? e.message : String(e));
        setAdding(false);
        return;
      } finally {
        setAdding(false);
      }
    }
    setCandidates((prev) => [...prev, raw]);
    setSelectedEmails((prev) => new Set(prev).add(raw));
    setAddEmailInput("");
  };

  const doSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await preview.onSend(body.trim(), selectedList);
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 210, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "5vh 1rem", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface,#fff)", borderRadius: 12, maxWidth: 720, width: "100%", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h3 style={{ margin: 0, fontWeight: 800 }}>📧 送信前の確認・編集 — {preview.title}</h3>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: "1.2rem", cursor: "pointer", color: "var(--text-secondary,#5f635f)" }}>✕</button>
        </div>

        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.35rem", fontWeight: 700 }}>
            送信先（チェックを外すと今回の送信から除外。任意の宛先を追加可）
          </div>
          {candidates.length === 0 ? (
            <div style={{ fontSize: "0.82rem", color: "var(--text-secondary,#5f635f)" }}>まだ宛先がありません。下の欄で追加してください。</div>
          ) : (
            <div className="support-status-change-modal__recipients">
              {candidates.map((email) => (
                <label key={email} className="support-status-change-modal__recipient" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(email)}
                    onChange={() => toggleEmail(email)}
                  />
                  <span className="mono">{email}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", alignItems: "center" }}>
            <input
              type="email"
              value={addEmailInput}
              onChange={(e) => setAddEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void doAddEmail(); } }}
              placeholder="宛先を追加（例: cc@example.com）"
              style={{ flex: 1, padding: "0.4rem 0.6rem", border: "1.5px solid var(--border,#e0e4e0)", borderRadius: 6, fontSize: "0.82rem", fontFamily: "inherit" }}
            />
            <button
              type="button"
              onClick={() => void doAddEmail()}
              disabled={adding || !addEmailInput.trim()}
              style={{ padding: "0.4rem 0.75rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", background: "var(--surface,#fff)", fontWeight: 700, fontSize: "0.8rem", cursor: adding ? "default" : "pointer", whiteSpace: "nowrap" }}
            >
              {adding ? "追加中…" : "追加"}
            </button>
          </div>
          {preview.onPersistEmail && (
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginTop: "0.4rem", fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={persistToTicket}
                onChange={(e) => setPersistToTicket(e.target.checked)}
              />
              追加した宛先をチケットの連絡先にも保存する
            </label>
          )}
          {addErr && (
            <div style={{ fontSize: "0.78rem", color: "var(--danger-ink)", marginTop: "0.35rem" }}>{addErr}</div>
          )}
          <div style={{ fontSize: "0.78rem", marginTop: "0.45rem", color: "var(--text)" }}>
            <span style={{ color: "var(--text-secondary,#5f635f)" }}>今回の送信先: </span>
            <span className="mono" style={{ fontWeight: 700 }}>{toLine || "（未選択）"}</span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.3rem 0.75rem", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
          <span style={{ color: "var(--text-secondary,#5f635f)" }}>件名</span>
          <span style={{ color: "var(--text)", fontWeight: 700 }}>{subjectLine}</span>
          {rendered && (
            <>
              <span style={{ color: "var(--text-secondary,#5f635f)" }}>返信先</span>
              <span className="mono" style={{ color: "var(--text-secondary,#5f635f)" }}>{rendered.reply_to}</span>
            </>
          )}
        </div>

        {blocked && (
          <div style={{ fontSize: "0.8rem", color: "var(--danger-ink)", background: "var(--danger-light)", border: "1px solid #f5c6c2", borderRadius: 6, padding: "0.5rem 0.7rem", marginBottom: "0.6rem" }}>
            ⚠️ {rendered?.suppressed_text}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
          <span style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>本文（送信前に編集できます）</span>
          {canPreview && (
            <label
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", cursor: "pointer" }}
              title="メールの一番上に「〇〇 様」を入れます（連絡担当者名が空なら「ご担当者様」）"
            >
              <input
                type="checkbox"
                checked={salutationOn}
                onChange={(e) => setBody(setSalutation(body, e.target.checked))}
              />
              宛名を入れる
              {rendered?.salutation && (
                <span style={{ color: "var(--primary)", fontWeight: 700 }}>（{rendered.salutation.trim()}）</span>
              )}
            </label>
          )}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          autoFocus
          style={{ width: "100%", padding: "0.6rem 0.75rem", border: "1.5px solid var(--border,#e0e4e0)", borderRadius: 8, fontSize: "0.9rem", fontFamily: "inherit", lineHeight: 1.7, resize: "vertical" }}
        />

        {canPreview && (
          <div style={{ marginTop: "0.75rem" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.3rem" }}>
              実際に届くメール{rendered ? "" : "（読み込み中…）"}
            </div>
            {renderErr ? (
              <div style={{ fontSize: "0.78rem", color: "var(--danger-ink)" }}>プレビューを取得できませんでした: {renderErr}</div>
            ) : (
              <div style={{ background: "var(--bg,#f9fbf9)", border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem 0.9rem", maxHeight: 260, overflowY: "auto", fontSize: "0.83rem", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {rendered?.salutation && (
                  <span style={{ color: "var(--primary)" }}>{rendered.salutation}</span>
                )}
                {rendered?.preamble && (
                  <span style={{ color: "var(--warning-ink)", background: "var(--warning-light)" }}>{rendered.preamble}</span>
                )}
                <span style={{ color: "var(--text)" }}>{rendered?.main}</span>
                <span style={{ color: "var(--text-secondary,#5f635f)" }}>{rendered?.footer}</span>
              </div>
            )}
          </div>
        )}

        {preview.note && (
          <div style={{ fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", background: "var(--primary-light,#eaf4eb)", borderRadius: 6, padding: "0.5rem 0.7rem", marginTop: "0.6rem" }}>
            ⓘ {preview.note}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.9rem" }}>
          <button onClick={onClose} style={{ padding: "0.5rem 1.2rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", background: "var(--surface,#fff)", color: "var(--text-secondary,#5f635f)", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
            キャンセル
          </button>
          <button
            onClick={doSend}
            disabled={!canSend}
            style={{ padding: "0.5rem 1.4rem", borderRadius: 6, border: "none", background: canSend ? "var(--primary)" : "var(--border,#e0e4e0)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: canSend ? "pointer" : "default" }}
          >
            {sending ? "送信中…" : preview.sendLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== B4: 保存ビュー（localStorage） =====
type ViewCfg = {
  channelFilter?: string[];
  searchQ: string;
  tagFilter: string;
  sortKey: string;
  sortOrder: string;
  statusFilter?: string[];
  priorityFilter?: string[];
};
function SavedViews({ current, onApply }: { current: ViewCfg; onApply: (v: ViewCfg) => void }) {
  const KEY = "nx_support_saved_views";
  const [views, setViews] = useState<Record<string, ViewCfg>>(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
  });
  const persist = (v: Record<string, ViewCfg>) => { setViews(v); try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* ignore */ } };
  const names = Object.keys(views);
  const saveCurrent = () => {
    const name = window.prompt(
      "このビューの名前（例: 顧客・対応中のみ）\n\n保存される内容: チャネル/状態/優先度フィルタ、検索語、#タグ、並び順",
    );
    if (name?.trim()) persist({ ...views, [name.trim()]: current });
  };
  const removeView = () => {
    if (names.length === 0) return;
    const name = window.prompt(`削除するビュー名\n\n保存済み: ${names.join("、")}`);
    if (name?.trim() && views[name.trim()]) {
      const cp = { ...views };
      delete cp[name.trim()];
      persist(cp);
    }
  };
  return (
    <div
      className="support-saved-views"
      title="よく使う絞り込み（フィルタ・検索・タグ・並び順）をブラウザに名前付き保存し、ワンクリックで呼び出します"
    >
      <select
        className="support-page__select support-saved-views__select"
        value=""
        disabled={names.length === 0}
        title={names.length === 0 ? "「条件を保存」で最初のビューを作れます" : "保存済みビューを適用"}
        onChange={(e) => { const v = views[e.target.value]; if (v) onApply(v); e.target.value = ""; }}
      >
        <option value="">{names.length === 0 ? "保存ビューなし" : "保存ビュー…"}</option>
        {names.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <button type="button" className="support-saved-views__btn" title="今のフィルタ・検索・並び順を保存" onClick={saveCurrent}>
        条件を保存
      </button>
      {names.length > 0 && (
        <button type="button" className="support-saved-views__btn support-saved-views__btn--muted" title="保存ビューを削除" onClick={removeView}>
          削除
        </button>
      )}
    </div>
  );
}

// ===== 共通モーダル枠 =====
function ModalShell({ title, onClose, children, width = 720 }: { title: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 200, padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface,#fff)", borderRadius: 12, border: "1px solid var(--border,#e0e4e0)", width: "100%", maxWidth: width, padding: "1.25rem 1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: "1.3rem", cursor: "pointer", color: "var(--text-secondary,#5f635f)" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ===== B6: 分析ダッシュボード =====
function AnalyticsModal({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    api<Record<string, unknown>>(`/support/analytics?days=${days}`).then((d) => { if (live) { setData(d); setErr(""); } }).catch((e) => { if (live) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [days]);
  const tile = (label: string, val: React.ReactNode, unit = "") => (
    <div style={{ flex: "1 1 120px", background: "var(--bg,#f9fbf9)", border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem" }}>
      <div style={{ fontSize: "0.7rem", color: "var(--text-secondary,#5f635f)" }}>{label}</div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.4rem", fontWeight: 700, color: "var(--primary,#358b39)" }}>{val ?? "—"}<span style={{ fontSize: "0.6em" }}>{unit}</span></div>
    </div>
  );
  const g = (o: unknown) => Object.entries((o as Record<string, number>) || {}).map(([k, v]) => `${k}: ${v}`).join(" / ") || "—";
  const d = data || {};
  return (
    <ModalShell title="📊 サポート分析" onClose={onClose} width={760}>
      <div style={{ marginBottom: "0.75rem" }}>
        期間: {[7, 30, 90].map((n) => (
          <button key={n} onClick={() => setDays(n)} style={{ marginRight: 6, padding: "0.25rem 0.6rem", borderRadius: 6, border: "1px solid " + (days === n ? "var(--primary)" : "var(--border,#e0e4e0)"), background: days === n ? "var(--primary)" : "transparent", color: days === n ? "#fff" : "var(--text)", cursor: "pointer" }}>{n}日</button>
        ))}
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", marginBottom: "0.5rem" }}>{err}</div>}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {tile("総件数", String(d.total ?? "—"))}
        {tile("解決", String(d.resolved_count ?? "—"))}
        {tile("初回応答(平均)", d.frt_minutes != null ? String(d.frt_minutes) : "—", "分")}
        {tile("解決時間(平均)", d.resolution_minutes != null ? String(d.resolution_minutes) : "—", "分")}
        {tile("SLA達成率", d.sla_attainment_pct != null ? String(d.sla_attainment_pct) : "—", "%")}
        {tile("CSAT平均", d.csat_avg != null ? String(d.csat_avg) : "—", "/5")}
        {tile("CSAT回答率", d.csat_response_rate_pct != null ? String(d.csat_response_rate_pct) : "—", "%")}
        {tile("返信QA平均", d.qa_avg != null ? String(d.qa_avg) : "—", "/5")}
        {tile("QA低評価", d.qa_low != null ? String(d.qa_low) : "—", "件")}
      </div>
      <div style={{ fontSize: "0.82rem", lineHeight: 1.9 }}>
        <div><b>ステータス別:</b> {g(d.by_status)}</div>
        <div><b>チャネル別:</b> {g(d.by_channel)}</div>
        <div><b>優先度別:</b> {g(d.by_priority)}</div>
        <div><b>CSAT分布:</b> {g(d.csat_distribution)}（送信 {String(d.csat_sent ?? 0)} / 回答 {String(d.csat_responses ?? 0)}）</div>
      </div>
    </ModalShell>
  );
}

// ===== B2/B3: 自動化ルール管理 =====
type RuleRow = { id: number; name: string; enabled: boolean; sort_order: number; conditions: Record<string, unknown>; actions: Record<string, unknown> };
function RulesModal({ onClose, canWrite }: { onClose: () => void; canWrite: boolean }) {
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState('{\n  "name": "例: 充電トラブル→high",\n  "enabled": true,\n  "sort_order": 100,\n  "conditions": {"channel": "partner", "keyword": "充電"},\n  "actions": {"set_priority": "high", "add_tags": ["repair"], "assign": "least_loaded", "assign_pool": ["a@example.com","b@example.com"]}\n}');
  const load = useCallback(() => { api<RuleRow[]>("/support/rules").then(setRules).catch((e) => setErr(e instanceof Error ? e.message : String(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { const body = JSON.parse(draft); await api("/support/rules", { method: "POST", body: JSON.stringify(body) }); setErr(""); load(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const toggle = async (r: RuleRow) => { try { await api(`/support/rules/${r.id}`, { method: "PUT", body: JSON.stringify({ ...r, enabled: !r.enabled }) }); load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } };
  const del = async (r: RuleRow) => { if (!window.confirm(`ルール「${r.name}」を削除しますか？`)) return; try { await api(`/support/rules/${r.id}`, { method: "DELETE" }); load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } };
  return (
    <ModalShell title="⚙️ 自動化ルール（起票時に評価）" onClose={onClose} width={760}>
      {err && <div style={{ color: "var(--danger,#e53935)", marginBottom: "0.5rem", whiteSpace: "pre-wrap" }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1rem" }}>
        {rules.length === 0 && <div style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.85rem" }}>ルールはまだありません。</div>}
        {rules.map((r) => (
          <div key={r.id} style={{ border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.5rem 0.75rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span style={{ fontWeight: 700, opacity: r.enabled ? 1 : 0.5 }}>{r.name}</span>
            <span className="mono" style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)" }}>#{r.sort_order}</span>
            <span style={{ flex: 1, fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{JSON.stringify(r.conditions)} → {JSON.stringify(r.actions)}</span>
            {canWrite && <button onClick={() => toggle(r)} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: r.enabled ? "var(--primary-light,#eaf4eb)" : "transparent", cursor: "pointer", fontSize: "0.72rem" }}>{r.enabled ? "有効" : "無効"}</button>}
            {canWrite && <button onClick={() => del(r)} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "none", background: "var(--danger,#e53935)", color: "#fff", cursor: "pointer", fontSize: "0.72rem" }}>削除</button>}
          </div>
        ))}
      </div>
      {canWrite && (
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", marginBottom: "0.3rem" }}>新規ルール（JSON）</div>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={9} style={{ width: "100%", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem", padding: "0.5rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", resize: "vertical" }} />
          <div style={{ fontSize: "0.7rem", color: "var(--text-secondary,#5f635f)", margin: "0.3rem 0" }}>conditions: channel/category/priority/keyword（AND）。actions: set_status/set_priority/set_category/add_tags/assign(least_loaded or メール)/assign_pool。</div>
          <button onClick={create} style={{ padding: "0.4rem 0.9rem", borderRadius: 6, border: "none", background: "var(--primary,#358b39)", color: "#fff", fontWeight: 700, cursor: "pointer" }}>ルールを追加</button>
        </div>
      )}
    </ModalShell>
  );
}

// ===== 迷惑送信元（ブロック中）: 着信は迷惑タグ付きで自動クローズ・通知なし =====
type BlockedSenderRow = { id: number; email: string; source_ticket_number: string | null; created_by: string | null; created_at: string | null; hit_count: number; last_hit_at: string | null };
function BlockedSendersModal({ onClose, canWrite }: { onClose: () => void; canWrite: boolean }) {
  const [rows, setRows] = useState<BlockedSenderRow[]>([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api<BlockedSenderRow[]>("/support/blocked-senders").then((r) => { setRows(r); setErr(""); }).catch((e) => setErr(e instanceof Error ? e.message : String(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const unblock = async (r: BlockedSenderRow) => {
    if (!window.confirm(`${r.email} のブロックを解除しますか？\n以後の着信は通常どおり起票・通知されます。`)) return;
    try { await api(`/support/blocked-senders/${r.id}`, { method: "DELETE" }); load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const fmt = (s: string | null) => (s ? new Date(s).toLocaleString("ja-JP", { dateStyle: "short", timeStyle: "short" }) : "-");
  return (
    <ModalShell title="🚫 ブロック中の送信元" onClose={onClose} width={720}>
      <div style={{ fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.75rem" }}>
        「迷惑としてクローズ」した送信元です。以後の着信は起票されますが、迷惑タグ付きで自動クローズし、Chat 通知・自動返信は行いません（アドレス完全一致）。
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", marginBottom: "0.5rem", whiteSpace: "pre-wrap" }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {rows.length === 0 && <div style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.85rem" }}>ブロック中の送信元はありません。</div>}
        {rows.map((r) => (
          <div key={r.id} style={{ border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.5rem 0.75rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span className="mono" style={{ fontWeight: 700 }}>{r.email}</span>
            <span style={{ flex: 1, fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)" }}>
              {fmt(r.created_at)} 登録{r.source_ticket_number ? `（${r.source_ticket_number}）` : ""} / ブロック後の着信 {r.hit_count} 件{r.last_hit_at ? `・最終 ${fmt(r.last_hit_at)}` : ""}
            </span>
            {canWrite && <button onClick={() => unblock(r)} style={{ padding: "0.2rem 0.6rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.72rem" }}>解除</button>}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

// ===== C4: 現場予定一覧モーダル =====
function DispatchModal({ onClose, onOpenTicket }: { onClose: () => void; onOpenTicket: (num: string) => void }) {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(14);
  useEffect(() => {
    let live = true;
    api<Array<Record<string, unknown>>>(`/support/schedule?days=${days}`).then((r) => { if (live) { setRows(r); setErr(""); } }).catch((e) => { if (live) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [days]);
  return (
    <ModalShell title="🗓 現場予定" onClose={onClose} width={700}>
      <div style={{ marginBottom: "0.6rem" }}>今後 {[7, 14, 30].map((n) => <button key={n} onClick={() => setDays(n)} style={{ marginRight: 6, padding: "0.2rem 0.6rem", borderRadius: 6, border: "1px solid " + (days === n ? "var(--primary)" : "var(--border,#e0e4e0)"), background: days === n ? "var(--primary)" : "transparent", color: days === n ? "#fff" : "var(--text)", cursor: "pointer" }}>{n}日</button>)}</div>
      {err && <div style={{ color: "var(--danger,#e53935)" }}>{err}</div>}
      {rows.length === 0 && <div style={{ color: "var(--text-secondary,#5f635f)" }}>予定はありません。</div>}
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: "0.6rem", alignItems: "center", padding: "0.4rem 0", borderBottom: "1px solid var(--border,#e0e4e0)", fontSize: "0.82rem" }}>
          <span className="mono" style={{ minWidth: 130 }}>{r.scheduled_at ? new Date(String(r.scheduled_at)).toLocaleString() : "—"}</span>
          <span>{String(r.scheduled_tech || "未割当")}</span>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(r.company || "")} {String(r.title || "")}</span>
          <button onClick={() => { onOpenTicket(String(r.ticket_number)); onClose(); }} className="mono" style={{ border: "none", background: "transparent", color: "var(--primary)", cursor: "pointer", fontSize: "0.72rem" }}>{String(r.ticket_number)}</button>
        </div>
      ))}
    </ModalShell>
  );
}

// ===== RMA: 交換台帳（不良品↔代替品） =====
type ExchangeRow = {
  id: number; ticket_id: number | null; ticket_number: string | null;
  kind?: string | null;
  old_serial: string | null; new_serial: string | null; company: string | null; partner_name: string | null;
  status: string; shipped_at: string | null; received_at: string | null; ship_to_address: string | null;
  expected_return_at?: string | null; returned_at?: string | null;
  tracking_number?: string | null; order_number?: string | null;
  symptom: string | null; root_cause_primary: string | null; root_cause_deep: string | null; permanent_action: string | null;
  production_confirmed_at: string | null; source: string; needs_review: boolean; review_note: string | null;
};
function ExchangeEditRow({ ex, onSaved, onClose }: { ex: ExchangeRow; onSaved: () => void; onClose: () => void }) {
  const [d, setD] = useState({ ...ex });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inp = { padding: "0.35rem 0.5rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit", fontSize: "0.8rem", width: "100%" } as const;
  const field = (label: string, key: keyof ExchangeRow, placeholder = "") => (
    <div style={{ marginBottom: "0.4rem" }}>
      <div style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)", marginBottom: 2 }}>{label}</div>
      <input value={(d[key] as string) || ""} placeholder={placeholder} onChange={(e) => setD((p) => ({ ...p, [key]: e.target.value }))} style={inp} />
    </div>
  );
  const save = async () => {
    setBusy(true);
    try {
      await api(`/support/exchanges/${ex.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          new_serial: d.new_serial || null, company: d.company || null, partner_name: d.partner_name || null,
          status: d.status, shipped_at: d.shipped_at || null, received_at: d.received_at || null,
          ship_to_address: d.ship_to_address || null, root_cause_primary: d.root_cause_primary || null,
          root_cause_deep: d.root_cause_deep || null, permanent_action: d.permanent_action || null,
          needs_review: d.needs_review, review_note: d.review_note || null,
        }),
      });
      onSaved(); onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  return (
    <div style={{ border: "1px solid var(--primary,#358b39)", borderRadius: 8, padding: "0.75rem", marginTop: "0.4rem", background: "var(--bg,#f9fbf9)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
        {field("代替品シリアル", "new_serial", "NX...")}
        {field("会社名", "company")}
        {field("代理店名", "partner_name")}
        {field("代替品発送日", "shipped_at", "YYYY-MM-DD")}
        {field("不具合品回収日", "received_at", "YYYY-MM-DD")}
        <div style={{ marginBottom: "0.4rem" }}>
          <div style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)", marginBottom: 2 }}>ステータス</div>
          <select value={d.status} onChange={(e) => setD((p) => ({ ...p, status: e.target.value }))} style={inp}>
            {["open", "shipped", "received", "analyzed", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      {field("送付先住所", "ship_to_address")}
      {field("発生原因(一次)", "root_cause_primary")}
      <div style={{ marginBottom: "0.4rem" }}>
        <div style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)", marginBottom: 2 }}>詳細原因(真因)</div>
        <textarea value={d.root_cause_deep || ""} onChange={(e) => setD((p) => ({ ...p, root_cause_deep: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} />
      </div>
      <div style={{ marginBottom: "0.4rem" }}>
        <div style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)", marginBottom: 2 }}>恒久対策</div>
        <textarea value={d.permanent_action || ""} onChange={(e) => setD((p) => ({ ...p, permanent_action: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", marginBottom: "0.4rem" }}>
        <input type="checkbox" checked={!!d.needs_review} onChange={(e) => setD((p) => ({ ...p, needs_review: e.target.checked }))} />
        要確認のまま残す
      </label>
      {d.needs_review && field("要確認メモ", "review_note", "何が曖昧か")}
      {err && <div style={{ color: "var(--danger,#e53935)", fontSize: "0.75rem", marginBottom: "0.4rem" }}>{err}</div>}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button onClick={save} disabled={busy} style={{ padding: "0.35rem 0.9rem", borderRadius: 6, border: "none", background: "var(--primary,#358b39)", color: "#fff", fontWeight: 700, cursor: busy ? "wait" : "pointer", fontSize: "0.8rem" }}>{busy ? "保存中" : "保存"}</button>
        <button onClick={onClose} style={{ padding: "0.35rem 0.8rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.8rem" }}>閉じる</button>
      </div>
    </div>
  );
}
function ExchangesModal({ onClose, onOpenTicket }: { onClose: () => void; onOpenTicket: (num: string) => void }) {
  const [rows, setRows] = useState<ExchangeRow[]>([]);
  const [err, setErr] = useState("");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (reviewOnly) params.set("needs_review", "true");
    if (q.trim()) params.set("q", q.trim());
    api<ExchangeRow[]>(`/support/exchanges?${params.toString()}`).then((r) => { setRows(r); setErr(""); }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [reviewOnly, q]);
  useEffect(() => { load(); }, [load]);
  const statusColor = (s: string) => s === "closed" ? "var(--text-secondary,#5f635f)" : s === "analyzed" ? "var(--success,#00897b)" : "var(--primary,#358b39)";
  return (
    <ModalShell title="🔄 交換台帳（RMA）" onClose={onClose} width={880}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="会社名・代理店名・シリアル（表記ゆらぎ対応）" style={{ padding: "0.35rem 0.6rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", fontSize: "0.8rem", flex: 1, minWidth: 160 }} />
        <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
          要確認のみ
        </label>
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", marginBottom: "0.5rem" }}>{err}</div>}
      {rows.length === 0 && <div style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.85rem" }}>該当する交換レコードはありません。</div>}
      {rows.map((r) => (
        <div key={r.id} style={{ borderBottom: "1px solid var(--border,#e0e4e0)", padding: "0.5rem 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.82rem" }}>
            {r.needs_review && <span style={{ background: "var(--warning-light)", color: "var(--warning-ink)", border: "1px solid var(--warning,#ffb300)", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.68rem", fontWeight: 700 }}>要確認</span>}
            <span style={{ background: r.kind === "loaner" ? "#1565c0" : "var(--primary,#358b39)", color: "#fff", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.68rem", fontWeight: 700 }}>{r.kind === "loaner" ? "貸し出し" : "代替え"}</span>
            <span style={{ background: statusColor(r.status), color: "#fff", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.68rem", fontWeight: 700 }}>{r.status}</span>
            <span className="mono" style={{ fontWeight: 700 }}>{r.old_serial || "(不明)"}</span>
            <span style={{ color: "var(--text-secondary,#5f635f)" }}>→</span>
            <span className="mono">{r.new_serial || "未定"}</span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.company || ""}{r.partner_name ? `（${r.partner_name}）` : ""}</span>
            {r.ticket_number && <button onClick={() => { onOpenTicket(r.ticket_number as string); onClose(); }} className="mono" style={{ border: "none", background: "transparent", color: "var(--primary)", cursor: "pointer", fontSize: "0.72rem" }}>{r.ticket_number}</button>}
            {r.order_number && <span className="mono" style={{ fontSize: "0.65rem", color: "var(--text-secondary,#5f635f)" }}>{r.order_number}</span>}
            {r.tracking_number && <span className="mono" style={{ fontSize: "0.65rem" }}>{r.tracking_number}</span>}
            {r.kind === "loaner" && r.expected_return_at && <span style={{ fontSize: "0.65rem", color: "var(--text-secondary,#5f635f)" }}>返却予定 {r.expected_return_at}</span>}
            <span style={{ fontSize: "0.65rem", color: "var(--text-secondary,#5f635f)" }}>{r.source === "production_auto" ? "🏭自動" : r.source === "migrated" ? "📋移行" : "手動"}</span>
            <button onClick={() => setEditing(editing === r.id ? null : r.id)} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.72rem" }}>{editing === r.id ? "閉じる" : "編集"}</button>
          </div>
          {r.symptom && <div style={{ fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", marginTop: "0.2rem" }}>{r.symptom}</div>}
          {editing === r.id && <ExchangeEditRow ex={r} onSaved={load} onClose={() => setEditing(null)} />}
        </div>
      ))}
    </ModalShell>
  );
}

// ===== A4: 過去事例 RAG 検索 =====
function KbSearchModal({ initialQuery, onClose, onUse }: { initialQuery: string; onClose: () => void; onUse: (t: string) => void }) {
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<{ answer: string | null; sources: Array<Record<string, unknown>> } | null>(null);
  const [err, setErr] = useState("");
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setRes(await api<{ answer: string | null; sources: Array<Record<string, unknown>> }>("/support/ai/kb-search", { method: "POST", body: JSON.stringify({ query: q.trim(), answer: true }) })); setErr(""); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="🔎 過去事例から回答（AI）" onClose={onClose}>
      <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3} placeholder="調べたい内容" style={{ width: "100%", padding: "0.5rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit" }} />
      <div style={{ margin: "0.5rem 0" }}>
        <button onClick={run} disabled={busy} style={{ padding: "0.4rem 0.9rem", borderRadius: 6, border: "none", background: "var(--primary,#358b39)", color: "#fff", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}>{busy ? "検索中…" : "検索"}</button>
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", whiteSpace: "pre-wrap" }}>{err}</div>}
      {res && (
        <div>
          {res.answer && (
            <div style={{ background: "var(--primary-light,#eaf4eb)", border: "1px solid var(--primary,#358b39)", borderRadius: 8, padding: "0.75rem", whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: "0.85rem" }}>
              {res.answer}
              <div style={{ marginTop: "0.5rem" }}><button onClick={() => onUse(res.answer || "")} style={{ padding: "0.3rem 0.7rem", borderRadius: 6, border: "1px solid var(--primary)", background: "var(--surface,#fff)", color: "var(--primary)", cursor: "pointer", fontSize: "0.78rem" }}>この内容を下書きに使う</button></div>
            </div>
          )}
          <div style={{ marginTop: "0.6rem", fontSize: "0.8rem" }}>
            <b>類似事例:</b>
            {(res.sources || []).length === 0 && <span style={{ color: "var(--text-secondary,#5f635f)" }}> 見つかりませんでした（KB未構築の可能性。分析→ルール横の再構築が必要な場合あり）</span>}
            {(res.sources || []).map((s, i) => (
              <div key={i} style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.75rem" }}>・<span className="mono">{String(s.source_ref)}</span> {String(s.title || "")}（類似度 {String(s.score)}）</div>
            ))}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ===== AI サポートボット（Phase1: 社内検証） =====
type AiChatTurn = {
  role: "user" | "assistant";
  content: string;
  sources?: Array<{ source_ref: string; title?: string | null; score?: number }>;
  escalate?: { needed: boolean; options: string[]; reason?: string | null };
};

function AiChatModal({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<AiChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const userTurn: AiChatTurn = { role: "user", content: text };
    const pending = [...messages, userTurn];
    setMessages(pending);
    setInput("");
    setBusy(true);
    setErr("");
    try {
      const r = await api<{
        answer: string;
        sources: Array<{ source_ref: string; title?: string | null; score?: number }>;
        escalate: { needed: boolean; options: string[]; reason?: string | null };
      }>("/support/ai/chat", {
        method: "POST",
        body: JSON.stringify({
          messages: pending.map((m) => ({ role: m.role, content: m.content })),
          audience: "internal",
        }),
      });
      setMessages([
        ...pending,
        { role: "assistant", content: r.answer || "", sources: r.sources, escalate: r.escalate },
      ]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setMessages(pending);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="🤖 AIボット（検証）" onClose={onClose} width={640}>
      <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", lineHeight: 1.6 }}>
        解決済みチケット・FAQ・マニュアルを根拠に回答します。安全/機体不具合の話題は有人へ案内します（Phase1・社内のみ）。
      </p>
      <div
        ref={scrollRef}
        style={{
          maxHeight: "min(52vh, 420px)",
          overflowY: "auto",
          border: "1px solid var(--border,#e0e4e0)",
          borderRadius: 8,
          padding: "0.75rem",
          marginBottom: "0.75rem",
          background: "var(--bg,#f7f8f7)",
        }}
      >
        {messages.length === 0 && (
          <div style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.82rem" }}>
            例: EC-700 作業アプリのシリアル入力方法 / How do I access the service app?
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              marginBottom: "0.65rem",
              textAlign: m.role === "user" ? "right" : "left",
            }}
          >
            <div
              style={{
                display: "inline-block",
                maxWidth: "92%",
                textAlign: "left",
                padding: "0.55rem 0.75rem",
                borderRadius: 8,
                whiteSpace: "pre-wrap",
                lineHeight: 1.65,
                fontSize: "0.84rem",
                background: m.role === "user" ? "var(--primary-light,#eaf4eb)" : "var(--surface,#fff)",
                border: m.role === "user" ? "1px solid var(--primary,#358b39)" : "1px solid var(--border,#e0e4e0)",
              }}
            >
              {m.content}
            </div>
            {m.role === "assistant" && (m.sources?.length ?? 0) > 0 && (
              <div style={{ marginTop: "0.35rem", display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                {(m.sources || []).map((s, j) => (
                  <span
                    key={j}
                    className="mono"
                    style={{
                      fontSize: "0.68rem",
                      padding: "0.15rem 0.45rem",
                      borderRadius: 999,
                      background: "var(--surface,#fff)",
                      border: "1px solid var(--border,#e0e4e0)",
                      color: "var(--text-secondary,#5f635f)",
                    }}
                    title={s.title || undefined}
                  >
                    {s.source_ref}
                  </span>
                ))}
              </div>
            )}
            {m.role === "assistant" && m.escalate?.needed && (
              <div
                style={{
                  marginTop: "0.4rem",
                  padding: "0.45rem 0.6rem",
                  borderRadius: 6,
                  fontSize: "0.75rem",
                  background: "#fff8e1",
                  border: "1px solid #ffc107",
                  color: "#6d4c00",
                }}
              >
                ⚠️ 有人サポートへの案内が必要です
                {m.escalate.reason === "safety_topic" && "（安全/機体不具合の話題）"}
                {m.escalate.reason === "no_kb_match" && "（該当ナレッジなし — KBギャップに記録）"}
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div style={{ fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)" }}>回答を生成中…</div>
        )}
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", whiteSpace: "pre-wrap", marginBottom: "0.5rem", fontSize: "0.82rem" }}>{err}</div>}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={2}
          placeholder="質問を入力（Enter で送信、Shift+Enter で改行）"
          style={{ flex: 1, padding: "0.5rem", borderRadius: 6, border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit", fontSize: "0.85rem" }}
          disabled={busy}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !input.trim()}
          style={{
            alignSelf: "flex-end",
            padding: "0.45rem 0.9rem",
            borderRadius: 6,
            border: "none",
            background: "var(--primary,#358b39)",
            color: "#fff",
            fontWeight: 700,
            cursor: busy ? "wait" : "pointer",
            opacity: busy || !input.trim() ? 0.6 : 1,
          }}
        >
          送信
        </button>
      </div>
    </ModalShell>
  );
}

// ===== KB ギャップ一覧（AI ボット no_kb_match） =====
type ChatGapItem = {
  id: number;
  query_text: string;
  audience?: string;
  actor?: string | null;
  status: string;
  ask_count: number;
  last_asked_at?: string | null;
  created_at?: string | null;
};

function ChatGapModal({ onClose }: { onClose: () => void }) {
  const [filter, setFilter] = useState<"open" | "all" | "dismissed" | "faq_added">("open");
  const [items, setItems] = useState<ChatGapItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api<{ items: ChatGapItem[] }>(`/support/ai/chat/gaps?status=${filter}&limit=100`);
      setItems(r.items || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  const setStatus = async (id: number, status: string) => {
    try {
      await api(`/support/ai/chat/gaps/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const copyStub = async (g: ChatGapItem) => {
    const md = `## Q: ${g.query_text}\n\n（KB に該当なし — 要 FAQ 追記）\n\n> ギャップ: 質問 ${g.ask_count} 回 / 最終 ${g.last_asked_at || ""}\n`;
    try {
      await navigator.clipboard.writeText(md);
      setMsg("FAQ たたき台をコピーしました");
      setTimeout(() => setMsg(""), 2000);
    } catch {
      setMsg("コピーに失敗しました");
    }
  };

  return (
    <ModalShell title="📋 KB ギャップ（FAQ 優先度）" onClose={onClose} width={720}>
      <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", lineHeight: 1.6 }}>
        AI ボットで KB ヒット 0 だった質問です。回数が多い順に FAQ 追記の優先度を判断してください。
      </p>
      <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        {(["open", "all", "dismissed", "faq_added"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            style={{
              padding: "0.25rem 0.55rem",
              borderRadius: 6,
              border: "1px solid " + (filter === f ? "var(--primary)" : "var(--border,#e0e4e0)"),
              background: filter === f ? "var(--primary)" : "transparent",
              color: filter === f ? "#fff" : "var(--text)",
              cursor: "pointer",
              fontSize: "0.75rem",
            }}
          >
            {f === "open" ? "未対応" : f === "all" ? "すべて" : f === "dismissed" ? "却下" : "FAQ済"}
          </button>
        ))}
        <button type="button" onClick={() => void load()} disabled={busy} style={{ marginLeft: "auto", padding: "0.25rem 0.55rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.75rem" }}>
          {busy ? "更新中…" : "更新"}
        </button>
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", marginBottom: "0.5rem", fontSize: "0.82rem" }}>{err}</div>}
      {msg && <div style={{ color: "var(--primary,#358b39)", marginBottom: "0.5rem", fontSize: "0.78rem" }}>{msg}</div>}
      <div style={{ maxHeight: "min(52vh, 420px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {items.length === 0 && !busy && (
          <div style={{ color: "var(--text-secondary,#5f635f)", fontSize: "0.82rem" }}>該当するギャップはありません。</div>
        )}
        {items.map((g) => (
          <div key={g.id} style={{ border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.6rem 0.75rem", background: "var(--surface,#fff)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: "0.84rem", flex: "1 1 200px", lineHeight: 1.5 }}>{g.query_text}</span>
              <span className="mono" style={{ fontSize: "0.68rem", color: "var(--primary,#358b39)", fontWeight: 700 }}>{g.ask_count} 回</span>
            </div>
            <div style={{ fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)", marginTop: "0.25rem" }}>
              {g.last_asked_at ? `最終 ${g.last_asked_at.replace("T", " ").slice(0, 16)}` : ""}
              {g.actor ? ` · ${g.actor}` : ""}
              {g.status !== "open" ? ` · ${g.status}` : ""}
            </div>
            {g.status === "open" && (
              <div style={{ display: "flex", gap: "0.35rem", marginTop: "0.45rem", flexWrap: "wrap" }}>
                <button type="button" onClick={() => void copyStub(g)} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "1px solid var(--primary)", background: "transparent", color: "var(--primary)", cursor: "pointer", fontSize: "0.68rem" }}>FAQたたき台コピー</button>
                <button type="button" onClick={() => void setStatus(g.id, "faq_added")} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.68rem" }}>FAQ追記済</button>
                <button type="button" onClick={() => void setStatus(g.id, "dismissed")} style={{ padding: "0.2rem 0.5rem", borderRadius: 6, border: "none", background: "var(--bg,#f0f0f0)", cursor: "pointer", fontSize: "0.68rem", color: "var(--text-secondary,#5f635f)" }}>却下</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

// ===== FAQ 候補生成（解決済みチケット → SUPPORT_FAQ.md 草案） =====
type FaqSuggestItem = {
  ticket_id: number;
  ticket_number: string;
  title?: string | null;
  status?: string;
  priority?: string;
  eligible: boolean;
  skip_reason?: string | null;
  question?: string | null;
  answer?: string | null;
  markdown?: string | null;
};

function TicketFaqModal({ result, onClose }: { result: FaqSuggestItem; onClose: () => void }) {
  const [msg, setMsg] = useState("");
  const copy = async () => {
    if (!result.markdown) return;
    try {
      await navigator.clipboard.writeText(result.markdown);
      setMsg("クリップボードにコピーしました");
      setTimeout(() => setMsg(""), 2000);
    } catch {
      setMsg("コピーに失敗しました");
    }
  };
  return (
    <ModalShell title={`📝 FAQ化 — ${result.ticket_number}`} onClose={onClose} width={640}>
      {!result.eligible ? (
        <div style={{ fontSize: "0.85rem", color: "var(--text-secondary,#5f635f)" }}>
          FAQ 化できませんでした: {result.skip_reason || "対象外"}
        </div>
      ) : (
        <>
          <p style={{ margin: "0 0 0.5rem", fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)" }}>
            内容を確認のうえ <code>docs/SUPPORT_FAQ.md</code> に追記 → ingest してください。
          </p>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.8rem", lineHeight: 1.65, fontFamily: "inherit", background: "var(--bg,#f7f8f7)", border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem" }}>{result.markdown}</pre>
          <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button type="button" onClick={() => void copy()} style={{ padding: "0.4rem 0.85rem", borderRadius: 6, border: "none", background: "var(--primary,#358b39)", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "0.8rem" }}>Markdown をコピー</button>
            {msg && <span style={{ fontSize: "0.75rem", color: "var(--primary,#358b39)" }}>{msg}</span>}
          </div>
        </>
      )}
    </ModalShell>
  );
}

function FaqSuggestModal({ onClose, canRebuild }: { onClose: () => void; canRebuild: boolean }) {
  const [limit, setLimit] = useState(10);
  const [busy, setBusy] = useState(false);
  const [kbBusy, setKbBusy] = useState(false);
  const [err, setErr] = useState("");
  const [kbMsg, setKbMsg] = useState("");
  const [result, setResult] = useState<{
    items: FaqSuggestItem[];
    markdown_bundle: string;
    eligible_count: number;
    scanned: number;
  } | null>(null);

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      setResult(await api<{
        items: FaqSuggestItem[];
        markdown_bundle: string;
        eligible_count: number;
        scanned: number;
      }>("/support/ai/faq-suggest", {
        method: "POST",
        body: JSON.stringify({ limit }),
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setKbMsg("クリップボードにコピーしました");
      setTimeout(() => setKbMsg(""), 2000);
    } catch {
      setKbMsg("コピーに失敗しました");
    }
  };

  const rebuildKb = async () => {
    setKbBusy(true);
    setKbMsg("");
    try {
      const r = await api<{ ok: boolean; indexed: number; candidates?: number }>("/support/ai/kb-rebuild?limit=500", { method: "POST" });
      setKbMsg(`KB 再構築完了: ${r.indexed} 件を索引化`);
    } catch (e) {
      setKbMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setKbBusy(false);
    }
  };

  return (
    <ModalShell title="📝 FAQ 候補を生成" onClose={onClose} width={720}>
      <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", lineHeight: 1.6 }}>
        解決/クローズ済みチケットから FAQ 草案（Markdown）を生成します。内容を確認のうえ <code>docs/SUPPORT_FAQ.md</code> に追記 → ingest スクリプトで取込してください。
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.8rem" }}>対象件数:</span>
        {[5, 10, 15, 20].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setLimit(n)}
            style={{
              padding: "0.25rem 0.55rem",
              borderRadius: 6,
              border: "1px solid " + (limit === n ? "var(--primary)" : "var(--border,#e0e4e0)"),
              background: limit === n ? "var(--primary)" : "transparent",
              color: limit === n ? "#fff" : "var(--text)",
              cursor: "pointer",
              fontSize: "0.78rem",
            }}
          >
            {n}件
          </button>
        ))}
        <button
          type="button"
          onClick={() => void run()}
          disabled={busy}
          style={{
            marginLeft: "auto",
            padding: "0.4rem 0.9rem",
            borderRadius: 6,
            border: "none",
            background: "var(--primary,#358b39)",
            color: "#fff",
            fontWeight: 700,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "生成中…" : "生成"}
        </button>
      </div>
      {err && <div style={{ color: "var(--danger,#e53935)", whiteSpace: "pre-wrap", marginBottom: "0.5rem", fontSize: "0.82rem" }}>{err}</div>}
      {kbMsg && <div style={{ color: "var(--primary,#358b39)", marginBottom: "0.5rem", fontSize: "0.78rem" }}>{kbMsg}</div>}
      {result && (
        <>
          <div style={{ fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.5rem" }}>
            スキャン {result.scanned} 件 / FAQ 化可能 {result.eligible_count} 件
            {result.markdown_bundle && (
              <button
                type="button"
                onClick={() => void copyText(result.markdown_bundle)}
                style={{ marginLeft: "0.75rem", padding: "0.2rem 0.5rem", borderRadius: 6, border: "1px solid var(--primary)", background: "transparent", color: "var(--primary)", cursor: "pointer", fontSize: "0.72rem" }}
              >
                全文コピー
              </button>
            )}
          </div>
          <div style={{ maxHeight: "min(50vh, 400px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {result.items.map((item) => (
              <div
                key={item.ticket_id}
                style={{
                  border: "1px solid var(--border,#e0e4e0)",
                  borderRadius: 8,
                  padding: "0.6rem 0.75rem",
                  background: item.eligible ? "var(--surface,#fff)" : "var(--bg,#f7f8f7)",
                  opacity: item.eligible ? 1 : 0.85,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
                  <span className="mono" style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--primary)" }}>{item.ticket_number}</span>
                  <span style={{ fontSize: "0.75rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title || ""}</span>
                  {item.eligible && item.markdown && (
                    <button type="button" onClick={() => void copyText(item.markdown || "")} style={{ padding: "0.15rem 0.45rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer", fontSize: "0.68rem" }}>
                      コピー
                    </button>
                  )}
                </div>
                {item.eligible ? (
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.75rem", lineHeight: 1.6, fontFamily: "inherit", color: "var(--text)" }}>{item.markdown}</pre>
                ) : (
                  <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)" }}>スキップ: {item.skip_reason || "対象外"}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {canRebuild && (
        <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border,#e0e4e0)" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.4rem" }}>
            解決済みチケットを RAG 索引（<code>source_type=ticket</code>）へ反映 — manager / super のみ
          </div>
          <button
            type="button"
            onClick={() => void rebuildKb()}
            disabled={kbBusy}
            style={{ padding: "0.35rem 0.75rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: kbBusy ? "wait" : "pointer", fontSize: "0.78rem" }}
          >
            {kbBusy ? "KB 再構築中…" : "KB 再構築（kb-rebuild）"}
          </button>
        </div>
      )}
    </ModalShell>
  );
}

export default function Support({ onLogout }: { onLogout?: () => void }) {
  // 既定＝入電対応ビュー（device_monitor 非表示・未完了のみ・更新日降順）。07-21 に全件表示へ変えていたが
  // C1/C2 自動起票分離後は入電と機体監視を分け、解決済みのノイズも抑える（2026-07-27）。
  const [channelFilter, setChannelFilter] = useState<string[]>(() => [...DEFAULT_CHANNEL_FILTER]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [threadByTicket, setThreadByTicket] = useState<Record<number, number>>({});
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [reply, setReply] = useState("");
  const [replyToCustomer, setReplyToCustomer] = useState(false); // false=社内メモ / true=顧客へメール返信
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const [aiBusy, setAiBusy] = useState<null | "draft" | "summary" | "faq">(null); // AIアシスト実行中
  const [ticketFaqResult, setTicketFaqResult] = useState<FaqSuggestItem | null>(null);
  const [aiSummary, setAiSummary] = useState(""); // AI要約の表示
  const [statusChangeModal, setStatusChangeModal] = useState<{ ticket: Ticket; newStatus: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([]);
  const [liveBanner, setLiveBanner] = useState<string | null>(null);
  const [mergeSuggestNewTicket, setMergeSuggestNewTicket] = useState<Ticket | null>(null);
  const [mergeSuggestCandidates, setMergeSuggestCandidates] = useState<MergeSuggestionCandidate[]>([]);
  const [mergeSuggestPrimaryId, setMergeSuggestPrimaryId] = useState<number>(0);
  const [showUrgent, setShowUrgent] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showRegular, setShowRegular] = useState(false);
  const [followupOnly, setFollowupOnly] = useState(false);
  // 未割当のみ（改善 E）。BE に該当パラメータが無いためクライアント側で絞る
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // ペイン開閉（改善 B）。情報レールは既定で閉じ、会話に幅を譲る
  const [railOpen, setRailOpen] = useUiState<boolean>("rail.open", false);
  const [listCollapsed, setListCollapsed] = useUiState<boolean>("list.collapsed", false);
  // 1 クリック割当の取り消しトースト（改善 E）
  const [assignUndo, setAssignUndo] = useState<{ ticket: Ticket; prevAssignee: string | null; prevStatus: string } | null>(null);
  // 統合の取り消し（BE の support_merge_events を逆再生する）
  const [mergeUndo, setMergeUndo] = useState<{ eventIds: number[]; primary: string; count: number } | null>(null);
  const [mergeUndoBusy, setMergeUndoBusy] = useState(false);
  const now = useMinuteTick();
  const [myAssignedOnly, setMyAssignedOnly] = useState(false);
  const [hasOpenTasksOnly, setHasOpenTasksOnly] = useState(false);
  const [myTasksOpen, setMyTasksOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [tagFilter, setTagFilter] = useState(""); // B4: タグで絞り込み
  // ステータス・優先度フィルタ（複数選択可）。既定は未完了のみ（解決・クローズ非表示）。
  const [statusFilter, setStatusFilter] = useState<string[]>(() => [...DEFAULT_STATUS_FILTER]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [analyticsOpen, setAnalyticsOpen] = useState(false); // B6: 分析ダッシュボード
  const [rulesOpen, setRulesOpen] = useState(false); // B2/B3: 自動化ルール管理
  const [blockedSendersOpen, setBlockedSendersOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false); // A4: 過去事例RAG検索
  const [aiChatOpen, setAiChatOpen] = useState(false); // Phase1: AIボット（社内検証）
  const [faqSuggestOpen, setFaqSuggestOpen] = useState(false); // FAQ 候補生成
  const [chatGapOpen, setChatGapOpen] = useState(false); // KB ギャップ一覧
  const [dispatchOpen, setDispatchOpen] = useState(false); // C4: 現場予定一覧
  const [exchangesOpen, setExchangesOpen] = useState(false); // RMA: 交換台帳
  const [ticketViewers, setTicketViewers] = useState<string[]>([]); // B7: 同時対応中の他担当
  const [sortKey, setSortKey] = useState<TicketSortKey>(DEFAULT_SORT_KEY);
  const [sortOrder, setSortOrder] = useState<TicketSortOrder>(DEFAULT_SORT_ORDER);
  const [emailLog, setEmailLog] = useState<EmailLog[]>([]); // 配送ログ（選択チケット）
  const [showTemplates, setShowTemplates] = useState(false); // 通知テンプレ編集モーダル
  const [templates, setTemplates] = useState<NotifyTemplate[]>([]);
  const [transitions, setTransitions] = useState<Record<string, string[]>>({}); // 遷移ルール（許可先）
  const [preview, setPreview] = useState<SendPreview | null>(null); // 送信プレビュー（宛先/件名/本文を確認・編集）
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set()); // 一括削除/マージの選択（manager/SUのみ）
  const [mergeOpen, setMergeOpen] = useState(false); // マージの存続先選択パネル開閉
  const [mergePrimaryId, setMergePrimaryId] = useState<number | null>(null); // マージの存続先（primary）
  const [showTriage, setShowTriage] = useState(false); // トリアージ編集パネルの開閉
  const [moreActionsOpen, setMoreActionsOpen] = useState(false); // トリアージ/訂正メール等
  const [aiMenuOpen, setAiMenuOpen] = useState(false); // AIアシストメニュー
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false); // 予定/交換/分析/ルール/通知テンプレをまとめた「ツール」メニュー
  const [commandBarExpanded, setCommandBarExpanded] = useState(() => {
    try { return localStorage.getItem("nx_support_command_bar") === "1"; } catch { return false; }
  });
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [urgentInitialType, setUrgentInitialType] = useState("設置トラブル");
  const createMenuRef = useRef<HTMLDivElement | null>(null);
  const toolsMenuRef = useRef<HTMLDivElement | null>(null);
  const moreActionsRef = useRef<HTMLDivElement | null>(null);
  const aiMenuRef = useRef<HTMLDivElement | null>(null);
  const msgEndRef = useRef<HTMLDivElement | null>(null);
  const channelFilterRef = useRef<string[]>(channelFilter);
  channelFilterRef.current = channelFilter;
  const searchQRef = useRef(searchQ);
  searchQRef.current = searchQ;
  const loadTicketsSeqRef = useRef(0);
  const [isSearchComposing, setIsSearchComposing] = useState(false);
  const tagFilterRef = useRef(tagFilter);
  tagFilterRef.current = tagFilter;
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;
  const priorityFilterRef = useRef(priorityFilter);
  priorityFilterRef.current = priorityFilter;
  const myAssignedOnlyRef = useRef(myAssignedOnly);
  myAssignedOnlyRef.current = myAssignedOnly;
  const hasOpenTasksOnlyRef = useRef(hasOpenTasksOnly);
  hasOpenTasksOnlyRef.current = hasOpenTasksOnly;
  const sortKeyRef = useRef(sortKey);
  sortKeyRef.current = sortKey;
  const sortOrderRef = useRef(sortOrder);
  sortOrderRef.current = sortOrder;
  const selectedRef = useRef<Ticket | null>(selected);
  selectedRef.current = selected;
  const threadByTicketRef = useRef<Record<number, number>>(threadByTicket);
  threadByTicketRef.current = threadByTicket;

  const author = useMemo(
    () => getRegisterAdminEmail() || `ops:${getRegisterAdminRole() || "staff"}`,
    [],
  );
  const [currentAuthorEmail, setCurrentAuthorEmail] = useState<string | null>(() => getRegisterAdminEmail());
  const [currentAuthorRole, setCurrentAuthorRole] = useState<string | null>(() => getRegisterAdminRole());
  const currentAuthorEmailRef = useRef<string | null>(currentAuthorEmail);
  currentAuthorEmailRef.current = currentAuthorEmail;

  useEffect(() => {
    const sync = () => {
      setCurrentAuthorEmail(getRegisterAdminEmail());
      setCurrentAuthorRole(getRegisterAdminRole());
    };
    sync();
    window.addEventListener("nx:register-session-updated", sync);
    return () => window.removeEventListener("nx:register-session-updated", sync);
  }, []);
  // 削除は manager / super のみ（support ロールでは掃除できない＝誤操作・証跡消し防止）
  const canDelete = useMemo(() => {
    const r = getRegisterAdminRole() || "";
    return r === "super" || r === "manager";
  }, []);
  const canWrite = useMemo(() => {
    const r = getRegisterAdminRole() || "";
    return r !== "viewer";
  }, []);
  // 区分(channel)の訂正: manager 以上は全変更可。support 等は device_monitor→手動チャネルの再分類のみ。
  // BE の can_change_ticket_channel（register_auth.py）と同じ範囲を保つこと。
  const canEditChannelFor = useCallback((t: Ticket) => canEditTicketChannel(t), []);

  const clearMergeSuggest = useCallback(() => {
    setMergeSuggestNewTicket(null);
    setMergeSuggestCandidates([]);
    setMergeSuggestPrimaryId(0);
  }, []);

  const offerMergeSuggest = useCallback(async (ticket: Ticket) => {
    const mac = (ticket.device_mac || "").trim();
    if (!mac) return;
    try {
      const candidates = await fetchMergeSuggestions(mac, ticket.id);
      if (!candidates.length) return;
      setMergeSuggestNewTicket(ticket);
      setMergeSuggestCandidates(candidates);
      setMergeSuggestPrimaryId(candidates[0].id);
    } catch {
      /* サジェスト失敗は起票自体には影響させない */
    }
  }, []);

  // 社内トリアージ: saveTriage は loadTickets 定義後（下記）

  // 連絡先（メール/電話の複数管理）の保存。エラーは呼び出し元（ContactsPanel）で表示するため
  // ここでは握りつぶさず投げる。
  const saveContacts = useCallback(async (t: Ticket, fields: { contact_emails: string[]; contact_phones: string[] }) => {
    const updated = await api<Ticket>(`/support/tickets/${t.id}`, { method: "PATCH", body: JSON.stringify(fields) });
    setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
    setSelected(updated);
  }, []);

  const handleScheduleSaved = useCallback((result: ScheduleSaveResult) => {
    if (!selected) return;
    const updated: Ticket = {
      ...selected,
      scheduled_at: result.scheduled_at,
      scheduled_tech: result.scheduled_tech,
      visit_status: result.visit_status,
      schedule_note: result.schedule_note,
    };
    setSelected(updated);
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    if (result.thread_message) {
      applyThreadMessageToTimeline(setMessages, result);
    }
  }, [selected]);

  const saveAssignee = useCallback(async (t: Ticket, email: string | null) => {
    try {
      const updated = await api<Ticket>(`/support/tickets/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({ assigned_to: email || "" }),
      });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      // 一覧から別チケットを割り当てたときに選択中のチケットを差し替えない
      if (selectedRef.current?.id === t.id) setSelected(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const saveTitle = useCallback(async (t: Ticket, title: string) => {
    const next = title.trim();
    if (!next) throw new Error("件名は空にできません");
    if (next === (t.title || "").trim()) return;
    try {
      const updated = await api<Ticket>(`/support/tickets/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({ title: next }),
      });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      setSelected(updated);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      throw e instanceof Error ? e : new Error(msg);
    }
  }, []);

  // 遷移ルール（許可先）＋通知テンプレをマウント時に取得。取得失敗しても致命ではない（fail-open）。
  useEffect(() => {
    api<{ users: MentionUser[] }>(`/support/mention-users`)
      .then((d) => setMentionUsers(d.users || []))
      .catch(() => setMentionUsers([]));
    api<{ allowed: Record<string, string[]> }>(`/support/status-transitions`)
      .then((d) => setTransitions(d.allowed || {}))
      .catch(() => setTransitions({}));
    api<NotifyTemplate[]>(`/support/notify-templates`).then(setTemplates).catch(() => {});
  }, []);

  // ch を省略すると現在のチャネルフィルタ（ref）を使う。作成直後の「今できたチケットの
  // チャネルへ即切替」等、state更新の反映を待たず明示的に指定したい呼び出しのみ引数で渡す。
  const loadTickets = useCallback(async (chArg?: string[]) => {
    const seq = ++loadTicketsSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const ch = chArg ?? channelFilterRef.current;
      // 空選択＝全チャネル。両APIとも channel 未指定＝全チャネルを返す仕様。
      // sla_urgency はクライアント側ソート。取得は sla_due_at 昇順（NULL は Postgres 既定で最後）
      const beSort = sortKeyRef.current === "sla_urgency" ? "sla_due_at" : sortKeyRef.current;
      const beOrder = sortKeyRef.current === "sla_urgency" ? "asc" : sortOrderRef.current;
      const params = new URLSearchParams({
        limit: "100",
        sort: beSort,
        order: beOrder,
      });
      const qv = searchQRef.current.trim();
      if (ch.length > 0 && !qv) params.set("channel", ch.join(","));
      if (qv) params.set("q", qv);
      const tg = tagFilterRef.current.trim();
      if (tg) params.set("tag", tg);
      if (statusFilterRef.current.length > 0) params.set("status", statusFilterRef.current.join(","));
      if (priorityFilterRef.current.length > 0) params.set("priority", priorityFilterRef.current.join(","));
      if (myAssignedOnlyRef.current && currentAuthorEmailRef.current) {
        params.set("assigned_to", currentAuthorEmailRef.current);
      }
      if (hasOpenTasksOnlyRef.current) params.set("has_open_tasks", "true");
      const threadParams = new URLSearchParams({ limit: "200" });
      // スレッドは ticket.id 紐付け用。チャネルで絞ると「顧客」フィルタ時に社内自動起票等の
      // スレッドが map に入らず「スレッドが見つかりません」になる。
      const [ts, threads] = await Promise.all([
        api<Ticket[]>(`/support/tickets?${params.toString()}`),
        api<Thread[]>(`/chat/threads?${threadParams.toString()}`),
      ]);
      // 古いリクエストのレスポンスが後から届いても、最新の検索結果を上書きしないようにする
      // （IME変換中の中間状態など、短時間に複数回検索が発火した場合の取りこぼし対策）。
      if (seq !== loadTicketsSeqRef.current) return;
      // closed の孤立スレッド（統合の残骸）を掴まないよう生存スレッドを優先する
      const map = buildThreadByTicket(threads);
      setTickets(ts);
      setThreadByTicket(map);
    } catch (e) {
      if (seq !== loadTicketsSeqRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === loadTicketsSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets(channelFilter);
    setSelected(null);
    setMessages([]);
  }, [channelFilter, loadTickets]);

  // 検索・ソート・チャネル/ステータス/優先度フィルタ変更時は再取得（検索はタイピング中の連打を避けて300msデバウンス。
  // クリックで変わるフィルタ類も同じ経路に乗せているが300msは体感できないため問題ない）。
  // IME変換中（isSearchComposing）は変換途中のひらがな/ローマ字が確定文字扱いで送信されて
  // しまうため取得を止め、変換確定（compositionend）後にまとめて1回取得する。
  useEffect(() => {
    if (isSearchComposing) return;
    const timer = window.setTimeout(() => loadTickets(), 300);
    return () => window.clearTimeout(timer);
  }, [searchQ, tagFilter, sortKey, sortOrder, statusFilter, priorityFilter, myAssignedOnly, hasOpenTasksOnly, loadTickets, isSearchComposing]);

  const saveTriage = useCallback(async (t: Ticket, fields: Record<string, string>, channel?: Channel) => {
    try {
      const body: Record<string, string> = { ...fields };
      if (channel && channel !== t.channel) body.channel = channel;
      const updated = await api<Ticket>(`/support/tickets/${t.id}`, { method: "PATCH", body: JSON.stringify(body) });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      setSelected(updated);
      setShowTriage(false);
      if (channel && channel !== t.channel) void loadTickets();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [loadTickets]);

  const saveChannel = useCallback(async (t: Ticket, channel: Channel) => {
    if (channel === t.channel) return;
    try {
      const updated = await api<Ticket>(`/support/tickets/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({ channel }),
      });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      setSelected(updated);
      void loadTickets();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [loadTickets]);

  const handleTasksChanged = useCallback((result?: SupportTask) => {
    void loadTickets();
    applyThreadMessageToTimeline(setMessages, result);
    if (selected && result) {
      const delta = result.status === "done" ? -1 : result.status === "open" ? 1 : 0;
      if (delta !== 0) {
        const nextCount = Math.max(0, (selected.open_task_count ?? 0) + delta);
        const updated = { ...selected, open_task_count: nextCount };
        setSelected(updated);
        setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      }
    }
  }, [loadTickets, selected]);

  const handleAssetChanged = useCallback((result?: HasThreadMessage) => {
    applyThreadMessageToTimeline(setMessages, result);
  }, []);

  const toggleStatusFilter = (s: string) =>
    setStatusFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const togglePriorityFilter = (p: string) =>
    setPriorityFilter((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const toggleChannelFilter = (c: string) =>
    setChannelFilter((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const clearAllFilters = useCallback(() => {
    setChannelFilter([...DEFAULT_CHANNEL_FILTER]);
    setStatusFilter([...DEFAULT_STATUS_FILTER]);
    setPriorityFilter([]);
    setFollowupOnly(false);
    setMyAssignedOnly(false);
    setHasOpenTasksOnly(false);
    setSortKey(DEFAULT_SORT_KEY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setSearchQ("");
    setTagFilter("");
  }, []);

  const filtersAtDefault = useMemo(() => {
    const setEq = (a: string[], b: readonly string[]) =>
      a.length === b.length && b.every((x) => a.includes(x));
    return (
      setEq(channelFilter, DEFAULT_CHANNEL_FILTER)
      && setEq(statusFilter, DEFAULT_STATUS_FILTER)
      && priorityFilter.length === 0
      && !followupOnly
      && !myAssignedOnly
      && !hasOpenTasksOnly
      && sortKey === DEFAULT_SORT_KEY
      && sortOrder === DEFAULT_SORT_ORDER
    );
  }, [
    channelFilter,
    statusFilter,
    priorityFilter,
    followupOnly,
    myAssignedOnly,
    hasOpenTasksOnly,
    sortKey,
    sortOrder,
  ]);

  const viewHasOverrides = useMemo(() => {
    return !filtersAtDefault || searchQ.trim() !== "" || tagFilter.trim() !== "";
  }, [filtersAtDefault, searchQ, tagFilter]);

  // 「ツール」ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    if (!toolsMenuOpen) return;
    const handler = (evt: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(evt.target as Node)) {
        setToolsMenuOpen(false);
      }
    };
    const id = window.setTimeout(() => document.addEventListener("click", handler), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", handler);
    };
  }, [toolsMenuOpen]);

  // ヘッダー「起票 ▾」の外側クリックで閉じる
  useEffect(() => {
    if (!createMenuOpen) return;
    const handler = (evt: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(evt.target as Node)) {
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [createMenuOpen]);

  const openCreateFollowup = useCallback(() => {
    setShowFollowup(true);
    setShowUrgent(false);
    setShowRegular(false);
    setCreateMenuOpen(false);
  }, []);

  const openCreateRegular = useCallback(() => {
    setShowRegular(true);
    setShowUrgent(false);
    setShowFollowup(false);
    setCreateMenuOpen(false);
  }, []);

  const openCreateUrgent = useCallback((type: string) => {
    setUrgentInitialType(type);
    setShowUrgent(true);
    setShowFollowup(false);
    setShowRegular(false);
    setCreateMenuOpen(false);
  }, []);

  const toggleCommandBar = useCallback((open: boolean) => {
    setCommandBarExpanded(open);
    try { localStorage.setItem("nx_support_command_bar", open ? "1" : "0"); } catch { /* ignore */ }
  }, []);

  // チケット詳細の「その他」「AI」メニュー: 外側クリックで閉じる
  useEffect(() => {
    if (!moreActionsOpen && !aiMenuOpen) return;
    const handler = (evt: MouseEvent) => {
      if (moreActionsOpen && moreActionsRef.current && !moreActionsRef.current.contains(evt.target as Node)) {
        setMoreActionsOpen(false);
      }
      if (aiMenuOpen && aiMenuRef.current && !aiMenuRef.current.contains(evt.target as Node)) {
        setAiMenuOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [moreActionsOpen, aiMenuOpen]);

  // チケット切替時に詳細メニューを閉じる
  useEffect(() => {
    setMoreActionsOpen(false);
    setAiMenuOpen(false);
  }, [selected?.id]);

  // B7: チケットを開いている間ハートビートし、同時に開いている他担当を検知
  const selectedId = selected?.id;
  useEffect(() => {
    if (selectedId == null) { setTicketViewers([]); return; }
    let live = true;
    const beat = () => api<{ others: string[] }>(`/support/tickets/${selectedId}/viewing`, { method: "POST" })
      .then((r) => { if (live) setTicketViewers(r.others || []); }).catch(() => { /* ignore */ });
    beat();
    const id = window.setInterval(beat, 15000);
    return () => { live = false; window.clearInterval(id); };
  }, [selectedId]);

  const goToDevice = useCallback(async (serial: string) => {
    try {
      const r = await api<{ device_mac: string | null }>(`/devices/resolve-serial?unit_serial=${encodeURIComponent(serial)}`);
      if (r.device_mac) {
        openDashMac(r.device_mac);
      } else {
        setError(`シリアル ${serial} に一致するデバイスが見つかりませんでした`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // 緊急のリアルタイム購読（本コードベース標準の /ws/{channel}?token= パターン）
  useEffect(() => {
    const token = getRegisterAdminToken();
    if (!token) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws/support?token=${encodeURIComponent(token)}`;
    let ws: WebSocket | null = null;
    let closed = false;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.type === "urgent_created") {
          const d = msg.data || {};
          setLiveBanner(`🚨 緊急: ${d.ticket_number} ${d.title || ""}`);
          // 受付ハブは入口で partner(No.1) / internal(社内) に振り分くので、緊急は代理店タブとは
          // 限らない。表示中のフィルタが対象を含みうるなら取り直す（空選択＝全チャネルも当然含む）。
          const cur = channelFilterRef.current;
          if (cur.length === 0 || cur.includes("partner") || cur.includes("internal") || cur.includes("service_campaign")) loadTickets();
        }
        if (msg?.type === "new_message") {
          const d = msg.data || {};
          const cur = selectedRef.current;
          // 開いているチケットのスレッド宛なら、その場でライブ追記（自分の送信分は id で重複排除）
          if (cur && threadByTicketRef.current[cur.id] === d.thread_id) {
            setMessages((prev) =>
              appendMessageUnique(prev, {
                id: Number(d.id),
                thread_id: Number(d.thread_id),
                message: d.message,
                author: d.author,
                source: d.source,
                // 欠落すると MessageThread が internal 扱い →「社内メモ」誤表示になる
                visibility: d.visibility,
                created_at: d.created_at,
              }),
            );
          }
        }
      } catch {
        /* location等の他メッセージは無視 */
      }
    };
    ws.onclose = () => {
      if (!closed) setLiveBanner((b) => b); // 明示的な再接続はしない（タブ再表示で再確立）
    };
    return () => {
      closed = true;
      ws?.close();
    };
  }, [loadTickets]);

  const createUrgent = useCallback(
    async (
      form: { title: string; trouble_type: string; description: string; partner_code: string; case_no: string; product: string; unit_serial: string; device_mac: string; contact_name: string; contact_affiliation: string; install_date: string; company: string; contact_emails: string[]; contact_phones: string[] },
      files: File[],
    ) => {
      try {
        const t = await api<Ticket>(`/support/urgent`, { method: "POST", body: JSON.stringify(form) });
        for (const file of files) {
          const fd = new FormData();
          fd.append("ticket_number", t.ticket_number);
          fd.append("file", file);
          try {
            await fetch(`/api/support/attachment`, { method: "POST", body: fd });
          } catch {
            /* 1件失敗しても継続 */
          }
        }
        setShowUrgent(false);
        setChannelFilter(["partner"]);
        loadTickets(["partner"]);
        await offerMergeSuggest(t);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadTickets, offerMergeSuggest],
  );

  const createFollowup = useCallback(
    async (form: {
      unit_serial: string; device_mac: string; product: string; company: string;
      contact_name: string; contact_emails: string[]; contact_phones: string[];
      items: { type: string; detail?: string; qty?: number }[]; revisit_period: string;
      customer_explained: boolean; notes: string;
    }) => {
      try {
        const t = await api<Ticket>(`/support/followup`, { method: "POST", body: JSON.stringify(form) });
        setShowFollowup(false);
        setChannelFilter(["partner"]);
        loadTickets(["partner"]);
        await offerMergeSuggest(t);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadTickets, offerMergeSuggest],
  );

  const createRegular = useCallback(
    async (form: {
      channel: string;
      title: string;
      description: string;
      category: string;
      priority: string;
      unit_serial: string;
      device_mac: string;
      product: string;
      company: string;
      contact_name: string;
      contact_emails: string[];
      contact_phones: string[];
    }) => {
      try {
        const t = await api<Ticket>(`/support/tickets`, {
          method: "POST",
          body: JSON.stringify({
            channel: form.channel,
            title: form.title,
            description: form.description || undefined,
            category: form.category || undefined,
            priority: form.priority || "normal",
            unit_serial: form.unit_serial || undefined,
            device_mac: form.device_mac || undefined,
            product: form.product || undefined,
            company: form.company || undefined,
            contact_name: form.contact_name || undefined,
            contact_emails: form.contact_emails.length ? form.contact_emails : undefined,
            contact_phones: form.contact_phones.length ? form.contact_phones : undefined,
          }),
        });
        setShowRegular(false);
        const ch = [form.channel as "partner" | "customer" | "internal"];
        setChannelFilter(ch);
        await loadTickets(ch);
        try {
          const threads = await api<Thread[]>(`/chat/threads?limit=200`);
          const th = pickTicketThread(threads, t.id);
          setSelected(t);
          if (th) {
            setThreadByTicket((prev) => ({ ...prev, [t.id]: th.id }));
            const msgs = await api<Message[]>(`/chat/threads/${th.id}/messages?limit=200`);
            setMessages(msgs);
          }
        } catch {
          setSelected(t);
        }
        setLiveBanner(`🎫 ${t.ticket_number} を起票しました`);
        await offerMergeSuggest(t);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadTickets, offerMergeSuggest],
  );

  const doMergeSuggest = useCallback(async () => {
    if (!mergeSuggestNewTicket || !mergeSuggestPrimaryId || !mergeSuggestCandidates.length) return;
    const primary = mergeSuggestCandidates.find((c) => c.id === mergeSuggestPrimaryId);
    if (!primary) return;
    if (!window.confirm(
      `存続: ${primary.ticket_number} ${primary.title}\n\n今回起票した ${mergeSuggestNewTicket.ticket_number} ${ticketDisplayTitle(mergeSuggestNewTicket)} を上記へ統合します（スレッド・メール履歴・連絡先を存続先へ集約）。\n\n統合しますか？`,
    )) return;
    try {
      const r = await api<MergeResponse>(`/support/tickets/merge`, {
        method: "POST",
        body: JSON.stringify({ primary_id: mergeSuggestPrimaryId, merge_ids: [mergeSuggestNewTicket.id] }),
      });
      clearMergeSuggest();
      loadTickets();
      const skip = r.skipped?.length ? `（${r.skipped.length} 件は統合済みのためスキップ）` : "";
      if (skip) setLiveBanner(`🔗 統合: ${skip}`);
      setMergeUndo({ eventIds: r.merge_event_ids || [], primary: r.primary, count: r.merged_count });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [mergeSuggestNewTicket, mergeSuggestPrimaryId, mergeSuggestCandidates, clearMergeSuggest, loadTickets]);

  // 一括削除（掃除用・manager/SU のみ）。loadTickets 定義後に置くこと。
  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const names = tickets.filter((t) => selectedIds.has(t.id)).map((t) => `・${t.ticket_number} ${ticketDisplayTitle(t)}`).join("\n");
    if (!window.confirm(`以下の ${ids.length} 件のチケットを完全に削除します。\n元に戻せません。\n\n${names}\n\n削除しますか？`)) return;
    try {
      const r = await api<{ deleted_count: number }>(`/support/tickets/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ ticket_ids: ids }),
      });
      setSelectedIds(new Set());
      setSelected(null);
      loadTickets();
      setLiveBanner(`🗑 ${r.deleted_count} 件のチケットを削除しました`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [selectedIds, tickets, loadTickets]);

  const bulkJunk = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const names = tickets.filter((t) => selectedIds.has(t.id)).map((t) => `・${t.ticket_number} ${ticketDisplayTitle(t)}`).join("\n");
    if (!window.confirm(`以下の ${ids.length} 件を迷惑メールとしてクローズします（削除はしません）。\n送信元もブロックし、以後の着信は通知なしで自動クローズします（解除: ツール → 🚫 ブロック中の送信元）。\n\n${names}\n\n実行しますか？`)) return;
    try {
      const r = await api<{ junked_count: number; blocked_senders?: string[] }>(`/support/tickets/bulk-junk`, {
        method: "POST",
        body: JSON.stringify({ ticket_ids: ids, block_senders: true }),
      });
      setSelectedIds(new Set());
      setSelected(null);
      loadTickets();
      const nBlocked = r.blocked_senders?.length ?? 0;
      setLiveBanner(`🚫 ${r.junked_count} 件を迷惑としてクローズしました${nBlocked ? `（送信元 ${nBlocked} 件をブロック）` : ""}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [selectedIds, tickets, loadTickets]);

  // マージ（統合・manager/SU のみ）。選択した中の 1 件を存続先（primary）にして残りを吸収する。
  const doMerge = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2 || mergePrimaryId == null) return;
    const primary = tickets.find((t) => t.id === mergePrimaryId);
    const others = tickets.filter((t) => selectedIds.has(t.id) && t.id !== mergePrimaryId);
    if (!primary || !others.length) return;
    const otherNames = others.map((t) => `・${t.ticket_number} ${ticketDisplayTitle(t)}`).join("\n");
    if (!window.confirm(
      `存続: ${primary.ticket_number} ${primary.title}\n\n以下 ${others.length} 件を上記へ統合します（スレッド・メール履歴・連絡先を存続先へ集約。統合された側は「統合済み」として残ります）。\n\n${otherNames}\n\n統合しますか？`
    )) return;
    try {
      const r = await api<MergeResponse>(`/support/tickets/merge`, {
        method: "POST",
        body: JSON.stringify({ primary_id: mergePrimaryId, merge_ids: others.map((t) => t.id) }),
      });
      setSelectedIds(new Set());
      setMergeOpen(false);
      setMergePrimaryId(null);
      setSelected(null);
      loadTickets();
      const skip = r.skipped?.length ? `（${r.skipped.length} 件は統合済みのためスキップ）` : "";
      if (skip) setLiveBanner(`🔗 統合: ${skip}`);
      setMergeUndo({ eventIds: r.merge_event_ids || [], primary: r.primary, count: r.merged_count });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [selectedIds, mergePrimaryId, tickets, loadTickets]);

  const fetchThreadIdForTicket = useCallback(async (ticketId: number): Promise<number | null> => {
    const cached = threadByTicketRef.current[ticketId];
    if (cached) return cached;
    try {
      const threads = await api<Thread[]>(`/chat/threads?limit=200`);
      const th = pickTicketThread(threads, ticketId);
      if (th) {
        setThreadByTicket((prev) => ({ ...prev, [ticketId]: th.id }));
        return th.id;
      }
    } catch {
      /* 一覧側で後から thread map が埋まる場合がある */
    }
    return null;
  }, []);

  // 詳細「その他」: 単票を迷惑メールとしてクローズ（一覧の一括と同じ bulk-junk + 送信元ブロック）
  const junkCloseTicket = useCallback(async (t: Ticket) => {
    if (t.status === "closed" && (t.tags || []).includes("迷惑")) return;
    if (!window.confirm(
      `${t.ticket_number} を迷惑メールとしてクローズします（削除はしません）。\n送信元もブロックし、以後の着信は通知なしで自動クローズします（解除: ツール → 🚫 ブロック中の送信元）。\n\n実行しますか？`,
    )) return;
    setMoreActionsOpen(false);
    try {
      const r = await api<{ junked_count: number; blocked_senders?: string[] }>(`/support/tickets/bulk-junk`, {
        method: "POST",
        body: JSON.stringify({ ticket_ids: [t.id], block_senders: true }),
      });
      const tags = Array.from(new Set([...(t.tags || []), "迷惑"]));
      const updated: Ticket = { ...t, status: "closed", tags };
      setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: "closed", tags } : x)));
      if (selectedRef.current?.id === t.id) {
        setSelected(updated);
        const threadId = await fetchThreadIdForTicket(t.id);
        if (threadId) {
          try {
            setMessages(await api<Message[]>(`/chat/threads/${threadId}/messages?limit=200`));
          } catch { /* 一覧再読込で追いつく */ }
        }
      }
      loadTickets();
      const nBlocked = r.blocked_senders?.length ?? 0;
      setLiveBanner(`🚫 ${t.ticket_number} を迷惑としてクローズしました${nBlocked ? `（送信元 ${nBlocked} 件をブロック）` : ""}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [loadTickets, fetchThreadIdForTicket]);

  const openTicket = useCallback(
    async (t: Ticket) => {
      setSelected(t);
      setMessages([]);
      setEmailLog([]);
      // チケット単位の下書きを復元（無ければ空）
      const draft = readUiState<{ text?: string } | null>(`reply.draft.${t.id}`, null);
      setReply(typeof draft?.text === "string" ? draft.text : "");
      setDraftSavedAt(null);
      setReplyToCustomer(false); // チケット切替時は安全側（社内メモ）に戻す
      setAiSummary(""); // チケット切替でAI要約はクリア
      setStatusChangeModal(null);
      // 配送ログ（送信成否）を取得（失敗しても致命でない）
      api<EmailLog[]>(`/support/tickets/${t.id}/email-log`).then(setEmailLog).catch(() => setEmailLog([]));
      const threadId = await fetchThreadIdForTicket(t.id);
      if (!threadId) {
        setError("このチケットに紐づくスレッドが見つかりません");
        return;
      }
      try {
        const msgs = await api<Message[]>(`/chat/threads/${threadId}/messages?limit=200`);
        setMessages(msgs);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [fetchThreadIdForTicket],
  );

  // 返信本文をチケット単位で随時自動保存（リロード・チケット再選択で復元）
  useEffect(() => {
    if (!selected) return;
    const tid = selected.id;
    const timer = window.setTimeout(() => {
      writeUiState(`reply.draft.${tid}`, { text: reply, at: Date.now() });
      setDraftSavedAt(Date.now());
    }, 600);
    return () => window.clearTimeout(timer);
  }, [reply, selected?.id]);

  // 深リンク等で openTicket 時点の thread map 未準備だった場合の救済
  useEffect(() => {
    const t = selected;
    if (!t || messages.length > 0) return;
    const threadId = threadByTicket[t.id];
    if (!threadId) return;
    let cancelled = false;
    api<Message[]>(`/chat/threads/${threadId}/messages?limit=200`)
      .then((msgs) => { if (!cancelled) setMessages(msgs); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selected?.id, threadByTicket, messages.length]);

  // Google Chat 等の深リンク: /console/?ticket=NX-S-…
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const ticketNo = new URLSearchParams(window.location.search).get("ticket")?.trim();
    if (!ticketNo) return;
    const found = tickets.find((x) => x.ticket_number === ticketNo);
    if (found) {
      deepLinkHandled.current = true;
      void openTicket(found);
      return;
    }
    if (loading) return;
    deepLinkHandled.current = true;
    void (async () => {
      try {
        const list = await api<Ticket[]>(
          `/support/tickets?q=${encodeURIComponent(ticketNo)}&limit=10&include_merged=true`,
        );
        const t = list.find((x) => x.ticket_number === ticketNo);
        if (!t) return;
        setTickets((prev) => (prev.some((x) => x.id === t.id) ? prev : [t, ...prev]));
        await openTicket(t);
      } catch {
        /* 一覧から手動で開いてもらう */
      }
    })();
  }, [tickets, loading, openTicket]);

  // 通知テンプレ編集モーダルを開くときに取得
  const openTemplates = useCallback(async () => {
    setShowTemplates(true);
    try {
      setTemplates(await api<NotifyTemplate[]>(`/support/notify-templates`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // テンプレ編集は TemplateModal 内で /support/mail-templates を直接叩く。
  // ここではステータス変更モーダルが使う一覧（旧 notify-templates 形式）を取り直すだけ。
  const refreshTemplates = useCallback(async () => {
    try {
      setTemplates(await api<NotifyTemplate[]>(`/support/notify-templates`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // 訂正運用: 誤送信の訂正メールを顧客へ送る（件名【訂正】）。送信プレビューで確認・編集してから送信。
  const sendCorrection = useCallback(async (t: Ticket) => {
    // 宛先判定は BE の recipient_addrs と同じ「複数連絡先を見る」挙動に合わせる
    const to = ticketContactEmails(t);
    // 件名・本文の初期値は訂正テンプレから（設定画面での編集・宛名チェックが効くようにする）
    let tplSubject = "【訂正】ご連絡内容の訂正";
    let tplBody = "";
    try {
      const d = await api<{ templates: MailTemplate[] }>(`/support/mail-templates`);
      const tpl = d.templates.find((x) => x.key === "correction");
      if (tpl) {
        tplSubject = tpl.subject_suffix || tplSubject;
        tplBody = tpl.body || "";
      }
    } catch { /* テンプレが取れなくても訂正は送れるようにする */ }
    setPreview({
      title: "訂正メールを送る",
      to: to.join(", "),
      candidateEmails: to,
      subject: `[${t.ticket_number}] ${tplSubject}`,
      body: tplBody,
      ticketId: t.id,
      kind: "correction",
      sendLabel: "【訂正】として顧客へ送信",
      onPersistEmail: async (email) => {
        const next = [...ticketContactEmails(t), email];
        const phones = t.contact_phones || [];
        await saveContacts(t, { contact_emails: next, contact_phones: phones });
        return next.map((e) => e.toLowerCase());
      },
      onSend: async (edited, toEmails) => {
        try {
          await api(`/support/tickets/${t.id}/correction`, {
            method: "POST",
            body: JSON.stringify({ body: edited, author, to_emails: toEmails }),
          });
          api<EmailLog[]>(`/support/tickets/${t.id}/email-log`).then(setEmailLog).catch(() => {});
          const threadId = threadByTicket[t.id];
          if (threadId) api<Message[]>(`/chat/threads/${threadId}/messages?limit=200`).then(setMessages).catch(() => {});
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      },
    });
  }, [author, threadByTicket, saveContacts]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 実際にスレッドへメッセージ送信（社内メモ or 顧客へメール）
  const postMessage = useCallback(async (
    body: string,
    toCustomer: boolean,
    attachmentFiles: File[] = [],
    toEmails?: string[],
  ) => {
    if (!selected) return;
    const threadId = threadByTicket[selected.id];
    if (!threadId) {
      setError("このチケットに紐づくスレッドが見つかりません");
      return;
    }
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      let messageBody = body.trim();
      if (attachmentFiles.length > 0) {
        const uploaded = await uploadTicketAttachments(selected.id, attachmentFiles);
        const attBlock = uploaded.map(formatAttachmentForMessage).join("\n");
        messageBody = messageBody ? `${messageBody}\n\n${attBlock}` : attBlock;
        const fresh = await api<Ticket>(`/support/tickets/${selected.id}`);
        setSelected(fresh);
        setTickets((prev) => prev.map((t) => (t.id === fresh.id ? fresh : t)));
      }
      if (!messageBody) return;
      const msg = await api<Message>(`/chat/threads/${threadId}/messages`, {
        method: "POST",
        body: JSON.stringify({
          message: messageBody,
          author,
          to_customer: toCustomer,
          ...(toCustomer && toEmails?.length ? { to_emails: toEmails } : {}),
        }),
      });
      setMessages((prev) => appendMessageUnique(prev, msg));
      setReply("");
      if (selected) writeUiState(`reply.draft.${selected.id}`, { text: "", at: Date.now() });
      setDraftSavedAt(null);
      try {
        const fresh = await api<Ticket>(`/support/tickets/${selected.id}`);
        setSelected(fresh);
        setTickets((prev) => prev.map((t) => (t.id === fresh.id ? fresh : t)));
      } catch { /* ignore */ }
      if (toCustomer) api<EmailLog[]>(`/support/tickets/${selected.id}/email-log`).then(setEmailLog).catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [selected, threadByTicket, author]);

  const sendReply = useCallback(async (attachmentFiles: File[] = [], composerToEmails?: string[]) => {
    if (!selected || (!reply.trim() && !attachmentFiles.length) || sendingRef.current) return;
    // 社内メモはそのまま送信。顧客へのメール返信は送信プレビューで確認・編集してから送信。
    if (!replyToCustomer) {
      await postMessage(reply.trim(), false, attachmentFiles);
      return;
    }
    const ticketEmails = ticketContactEmails(selected);
    const selectedEmails = (composerToEmails?.length ? composerToEmails : ticketEmails).map((e) => e.toLowerCase());
    const seen = new Set(ticketEmails);
    const candidates = [...ticketEmails];
    for (const e of selectedEmails) {
      if (e && !seen.has(e)) {
        seen.add(e);
        candidates.push(e);
      }
    }
    setPreview({
      title: "顧客へメール返信",
      to: selectedEmails.join(", "),
      candidateEmails: candidates,
      initialSelectedEmails: selectedEmails,
      subject: `[${selected.ticket_number}] ${selected.title || "お問い合わせ"}`,
      body: reply.trim(),
      ticketId: selected.id,
      kind: "reply",
      note: attachmentFiles.length
        ? "添付画像はメール本文にリンクとして含まれます（メールへのファイル同梱は未対応）。"
        : undefined,
      sendLabel: "この内容で顧客へ送信",
      onPersistEmail: async (email) => {
        const next = [...ticketContactEmails(selected), email];
        const phones = selected.contact_phones || [];
        await saveContacts(selected, { contact_emails: next, contact_phones: phones });
        return next.map((e) => e.toLowerCase());
      },
      onSend: (edited, toEmails) => postMessage(edited, true, attachmentFiles, toEmails),
    });
  }, [selected, reply, replyToCustomer, postMessage, saveContacts]);

  // AI: 返信ドラフト生成 → reply 欄へ流し込み（送信前に必ず人が確認・編集する）
  const aiDraft = useCallback(async () => {
    if (!selected || aiBusy) return;
    setAiBusy("draft");
    try {
      const r = await api<{ draft: string }>(`/support/tickets/${selected.id}/ai/draft`, {
        method: "POST",
        body: JSON.stringify({ instruction: reply.trim() || undefined }),
      });
      setReply(r.draft || "");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(null);
    }
  }, [selected, reply, aiBusy]);

  // AI: チケット要約 → 上部ボックスに表示
  const aiSummarize = useCallback(async () => {
    if (!selected || aiBusy) return;
    setAiBusy("summary");
    try {
      const r = await api<{ summary: string }>(`/support/tickets/${selected.id}/ai/summarize`, { method: "POST" });
      setAiSummary(r.summary || "");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(null);
    }
  }, [selected, aiBusy]);

  const aiFaqSuggest = useCallback(async () => {
    if (!selected || aiBusy) return;
    if (selected.status !== "resolved" && selected.status !== "closed") {
      setError("解決/クローズ済みチケットのみ FAQ 化できます");
      setAiMenuOpen(false);
      return;
    }
    setAiBusy("faq");
    setAiMenuOpen(false);
    try {
      setTicketFaqResult(await api<FaqSuggestItem>(`/support/tickets/${selected.id}/ai/faq-suggest`, { method: "POST" }));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(null);
    }
  }, [selected, aiBusy]);

  // ステータス変更を実行（通知本文は編集後の notify_message を渡せる）
  const applyStatus = useCallback(async (
    t: Ticket,
    status: string,
    opts?: { notifyBody?: string; notifyEmails?: string[] },
  ) => {
    try {
      const willNotify = opts?.notifyBody !== undefined;
      const updated = await api<Ticket & HasThreadMessage>(`/support/tickets/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status,
          notify_customer: willNotify,
          ...(willNotify ? { notify_message: opts?.notifyBody } : {}),
          ...(willNotify && opts?.notifyEmails?.length ? { notify_emails: opts.notifyEmails } : {}),
        }),
      });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      if (selectedRef.current?.id === t.id) {
        setSelected(updated);
        applyThreadMessageToTimeline(setMessages, updated);
        api<EmailLog[]>(`/support/tickets/${t.id}/email-log`).then(setEmailLog).catch(() => {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const updateStatus = useCallback((t: Ticket, status: string) => {
    if (status === t.status) return;
    setStatusChangeModal({ ticket: t, newStatus: status });
  }, []);

  const confirmStatusChange = useCallback(async (
    opts: { notify: boolean; notifyEmails: string[]; notifyBody?: string },
  ) => {
    if (!statusChangeModal) return;
    const { ticket: t, newStatus } = statusChangeModal;
    if (opts.notify && opts.notifyBody) {
      await applyStatus(t, newStatus, { notifyBody: opts.notifyBody, notifyEmails: opts.notifyEmails });
    } else {
      await applyStatus(t, newStatus);
    }
  }, [statusChangeModal, applyStatus]);

  // ── 一覧の派生データ（改善 D・E）─────────────────────────────
  /** 実際に描画するチケット。クライアント側の絞り込みと SLA 逼迫順ソートを適用する。 */
  const visibleTickets = useMemo(() => {
    let list = tickets;
    if (followupOnly) list = list.filter((t) => t.category === "後日対応");
    if (unassignedOnly) list = list.filter((t) => !t.assigned_to);
    if (sortKey === "sla_urgency") {
      const dir = sortOrder === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const av = slaSortValue(a, now);
        const bv = slaSortValue(b, now);
        // SLA 対象外（期限なし・応答済み）は昇順/降順どちらでも常に末尾
        if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0;
        if (!Number.isFinite(av)) return 1;
        if (!Number.isFinite(bv)) return -1;
        return (av - bv) * dir;
      });
    }
    return list;
  }, [tickets, followupOnly, unassignedOnly, sortKey, sortOrder, now]);

  /** リスト上部のサマリ（改善 D-3）。**読み込み済みの一覧**に対する件数。 */
  const listSummary = useMemo(() => {
    let over = 0;
    let unassigned = 0;
    let inProgress = 0;
    for (const t of tickets) {
      if (slaState(t, now).level === "over") over++;
      if (!t.assigned_to) unassigned++;
      if (t.status === "in_progress") inProgress++;
    }
    return { over, unassigned, inProgress };
  }, [tickets, now]);

  /** 要対応（未割当 かつ SLA 超過）。リスト先頭に固定表示する（最大5件・0件なら非表示）。 */
  const urgentPinned = useMemo(
    () => visibleTickets.filter((t) => !t.assigned_to && slaState(t, now).level === "over").slice(0, 5),
    [visibleTickets, now],
  );

  const toggleSelectedId = useCallback((id: number, on: boolean) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
  }, []);

  /** 「自分が対応」: 担当を自分にし、新規なら対応中へ進める。顧客への通知は送らない。 */
  const assignSelf = useCallback(async (t: Ticket) => {
    const me = currentAuthorEmailRef.current;
    if (!me) {
      setError("ログイン中のメールアドレスを取得できないため割当できません");
      return;
    }
    const prevAssignee = t.assigned_to || null;
    const prevStatus = t.status;
    try {
      const body: Record<string, unknown> = { assigned_to: me };
      // 新規のまま担当だけ付くと着手済みか分からないため対応中へ。
      // notify_customer を付けない = 顧客へのステータス通知メールは送信されない。
      if (t.status === "new") body.status = "in_progress";
      const updated = await api<Ticket & HasThreadMessage>(`/support/tickets/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      if (selectedRef.current?.id === t.id) {
        setSelected(updated);
        applyThreadMessageToTimeline(setMessages, updated);
      }
      setAssignUndo({ ticket: updated, prevAssignee, prevStatus });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  /** 取り消し。`in_progress → new` は遷移ルール上戻せないため、その場合は担当のみ戻す。 */
  const undoAssign = useCallback(async () => {
    const undo = assignUndo;
    if (!undo) return;
    setAssignUndo(null);
    const cur = undo.ticket;
    const canRevertStatus =
      cur.status === undo.prevStatus
      || (transitions[cur.status] || []).includes(undo.prevStatus);
    try {
      const updated = await api<Ticket>(`/support/tickets/${cur.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          assigned_to: undo.prevAssignee || "",
          ...(canRevertStatus ? { status: undo.prevStatus } : {}),
        }),
      });
      setTickets((prev) => prev.map((x) => (x.id === cur.id ? updated : x)));
      if (selectedRef.current?.id === cur.id) setSelected(updated);
      if (!canRevertStatus) {
        setLiveBanner(
          `担当を戻しました。ステータスは「${STATUS_LABEL[cur.status] || cur.status}」のままです`
          + `（「${STATUS_LABEL[undo.prevStatus] || undo.prevStatus}」へは戻せない遷移のため）`,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [assignUndo, transitions]);

  // 1200〜1599px では情報レールを開いたら一覧を自動で畳む（改善 B-3）
  useEffect(() => {
    if (!railOpen) return;
    if (window.matchMedia("(max-width: 1599px)").matches) setListCollapsed(true);
  }, [railOpen, setListCollapsed]);

  /** 統合の取り消し。BE が新しい順（末尾から）に逆再生するので event_ids をそのまま渡す。 */
  const undoMerge = useCallback(async () => {
    const undo = mergeUndo;
    if (!undo || !undo.eventIds.length || mergeUndoBusy) return;
    setMergeUndoBusy(true);
    try {
      const r = await api<MergeUndoResponse>(`/support/tickets/merge/undo`, {
        method: "POST",
        body: JSON.stringify({ event_ids: undo.eventIds }),
      });
      setMergeUndo(null);
      loadTickets();
      const skipped = r.skipped_fields?.length
        ? `（統合後に編集された項目は戻していません: ${r.skipped_fields.join(", ")}）`
        : "";
      setLiveBanner(`↩ 統合を取り消しました: ${r.undone.join("・")}${skipped}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMergeUndoBusy(false);
    }
  }, [mergeUndo, mergeUndoBusy, loadTickets]);

  // 取り消しトーストは 3 秒で消す
  useEffect(() => {
    if (!assignUndo) return;
    const id = window.setTimeout(() => setAssignUndo(null), 3000);
    return () => window.clearTimeout(id);
  }, [assignUndo]);

  const toolsMenu = (
    <>
      <button type="button" className="support-dropdown__item" onClick={() => { setMyTasksOpen(true); setToolsMenuOpen(false); }}>
        📋 自分のタスク
      </button>
      <button type="button" className="support-dropdown__item" onClick={() => { setDispatchOpen(true); setToolsMenuOpen(false); }}>
        🗓 現場予定
      </button>
      <button type="button" className="support-dropdown__item" onClick={() => { setExchangesOpen(true); setToolsMenuOpen(false); }}>
        🔄 交換台帳（RMA）
      </button>
      {canWrite && (
        <button type="button" className="support-dropdown__item" onClick={() => { setChatGapOpen(true); setToolsMenuOpen(false); }}>
          📋 KBギャップ
        </button>
      )}
      {canWrite && (
        <button type="button" className="support-dropdown__item" onClick={() => { setFaqSuggestOpen(true); setToolsMenuOpen(false); }}>
          📝 FAQ候補を生成
        </button>
      )}
      {canDelete && (
        <button type="button" className="support-dropdown__item" onClick={() => { setAnalyticsOpen(true); setToolsMenuOpen(false); }}>
          📊 分析ダッシュボード
        </button>
      )}
      {canDelete && (
        <button type="button" className="support-dropdown__item" onClick={() => { setRulesOpen(true); setToolsMenuOpen(false); }}>
          ⚙️ 自動化ルール
        </button>
      )}
      <button type="button" className="support-dropdown__item" onClick={() => { setBlockedSendersOpen(true); setToolsMenuOpen(false); }}>
        🚫 ブロック中の送信元
      </button>
      <button type="button" className="support-dropdown__item" onClick={() => { openTemplates(); setToolsMenuOpen(false); }}>
        ✉️ 通知テンプレート
      </button>
      <div className="support-dropdown__divider" role="separator" />
      <button
        type="button"
        className="support-dropdown__item"
        onClick={() => {
          toggleCommandBar(!commandBarExpanded);
          setToolsMenuOpen(false);
        }}
      >
        {commandBarExpanded ? "▴ 保存ビュー・タグ検索をたたむ" : "▾ 保存ビュー・タグ検索を表示"}
      </button>
    </>
  );

  return (
    <div className="support-page">
      <header className="support-page__header">
        <div className="support-page__header-brand">
          <a href="/console/" className="support-page__logo-link" aria-label="naturenix">
            <img
              src="/console/naturenix_logo_green.svg"
              alt="naturenix"
              className="support-page__logo"
            />
          </a>
          <div>
            <h2 className="support-page__title">サポート</h2>
            <p className="support-page__subtitle">代理店・最終顧客・社内のチャネル別にチケットとチャットを管理します。</p>
          </div>
        </div>
        <div className="support-page__header-actions">
          <button type="button" className="support-page__header-btn" onClick={() => loadTickets()} title="一覧を再取得">
            更新
          </button>
          {canWrite && (
            <button
              type="button"
              className={"support-page__header-btn" + (aiChatOpen ? " support-page__header-btn--active" : "")}
              onClick={() => setAiChatOpen(true)}
              title="KB 根拠付き AI ボット（社内検証）"
            >
              🤖 AIボット
            </button>
          )}
          <button
            type="button"
            className={"support-page__header-btn support-page__header-btn--urgent" + (showUrgent && urgentInitialType === "緊急トラブル対応" ? " support-page__header-btn--active" : "")}
            onClick={() => {
              if (showUrgent && urgentInitialType === "緊急トラブル対応") setShowUrgent(false);
              else openCreateUrgent("緊急トラブル対応");
            }}
            title="緊急トラブル対応を起票"
          >
            🚨 緊急
          </button>
          <div className="support-page__header-dropdown" ref={createMenuRef}>
            <button
              type="button"
              className={"support-page__header-btn support-page__header-btn--create" + (createMenuOpen || showFollowup || showRegular || (showUrgent && urgentInitialType === "設置トラブル") ? " support-page__header-btn--active" : "")}
              onClick={() => { setToolsMenuOpen(false); setCreateMenuOpen((v) => !v); }}
              title="通常起票・設置トラブル・後日対応"
            >
              起票 ▾
            </button>
            {createMenuOpen && (
              <div className="support-dropdown support-dropdown--align-right">
                <button type="button" className="support-dropdown__item" onClick={openCreateRegular}>
                  通常起票
                </button>
                <button type="button" className="support-dropdown__item" onClick={() => openCreateUrgent("設置トラブル")}>
                  設置トラブル（代理店・緊急）
                </button>
                <button type="button" className="support-dropdown__item" onClick={openCreateFollowup}>
                  後日対応
                </button>
              </div>
            )}
          </div>
          <div className="support-page__header-dropdown" ref={toolsMenuRef}>
            <button
              type="button"
              className={"support-page__header-btn" + (toolsMenuOpen || commandBarExpanded ? " support-page__header-btn--active" : "")}
              onClick={() => { setCreateMenuOpen(false); setToolsMenuOpen((v) => !v); }}
              title="現場予定・分析・並び替えなど"
            >
              ツール ▾
            </button>
            {toolsMenuOpen && (
              <div className="support-dropdown support-dropdown--align-right">
                {toolsMenu}
              </div>
            )}
          </div>
          {onLogout && (
            <button type="button" className="support-page__header-btn support-page__header-btn--logout" onClick={onLogout} title="ログアウト">
              ログアウト
            </button>
          )}
        </div>
      </header>

      {showTemplates && (
        <TemplateModal
          onClose={() => setShowTemplates(false)}
          onSaved={refreshTemplates}
        />
      )}

      {preview && <SendPreviewModal preview={preview} onClose={() => setPreview(null)} />}
      {statusChangeModal && (
        <StatusChangeModal
          ticket={statusChangeModal.ticket}
          newStatus={statusChangeModal.newStatus}
          templates={templates}
          onClose={() => setStatusChangeModal(null)}
          onConfirm={confirmStatusChange}
        />
      )}
      {analyticsOpen && <AnalyticsModal onClose={() => setAnalyticsOpen(false)} />}
      {rulesOpen && <RulesModal onClose={() => setRulesOpen(false)} canWrite={canDelete} />}
      {blockedSendersOpen && <BlockedSendersModal onClose={() => setBlockedSendersOpen(false)} canWrite={canWrite} />}
      {kbOpen && selected && <KbSearchModal initialQuery={`${selected.title || ""}\n${selected.description || ""}`.trim()} onClose={() => setKbOpen(false)} onUse={(t) => { setReply(t); setKbOpen(false); }} />}
      {aiChatOpen && <AiChatModal onClose={() => setAiChatOpen(false)} />}
      {faqSuggestOpen && <FaqSuggestModal onClose={() => setFaqSuggestOpen(false)} canRebuild={canDelete} />}
      {chatGapOpen && <ChatGapModal onClose={() => setChatGapOpen(false)} />}
      {ticketFaqResult && <TicketFaqModal result={ticketFaqResult} onClose={() => setTicketFaqResult(null)} />}
      {dispatchOpen && <DispatchModal onClose={() => setDispatchOpen(false)} onOpenTicket={(num) => { const t = tickets.find((x) => x.ticket_number === num); if (t) openTicket(t); }} />}
      {exchangesOpen && <ExchangesModal onClose={() => setExchangesOpen(false)} onOpenTicket={(num) => { const t = tickets.find((x) => x.ticket_number === num); if (t) openTicket(t); }} />}
      {myTasksOpen && (
        <MyTasksModal
          canWrite={canWrite}
          onClose={() => setMyTasksOpen(false)}
          onTaskChanged={handleTasksChanged}
          onOpenTicket={(num) => {
            const t = tickets.find((x) => x.ticket_number === num);
            if (t) void openTicket(t);
          }}
        />
      )}

      {showTriage && selected && (
        <TriageModal
          ticket={selected}
          onClose={() => setShowTriage(false)}
          onSave={saveTriage}
          canEditChannel={canEditChannelFor(selected)}
        />
      )}

      <div className={"support-page__command-bar" + (commandBarExpanded ? " support-page__command-bar--expanded" : " support-page__command-bar--collapsed")}>
        <div className="support-page__command-row">
          <FilterBar
            embedded
            channelFilter={channelFilter}
            statusFilter={statusFilter}
            priorityFilter={priorityFilter}
            followupOnly={followupOnly}
            myAssignedOnly={myAssignedOnly}
            hasOpenTasksOnly={hasOpenTasksOnly}
            onToggleChannel={toggleChannelFilter}
            onToggleStatus={toggleStatusFilter}
            onTogglePriority={togglePriorityFilter}
            onFollowupOnlyChange={setFollowupOnly}
            onMyAssignedOnlyChange={setMyAssignedOnly}
            onHasOpenTasksOnlyChange={setHasOpenTasksOnly}
            onClearChannels={() => setChannelFilter([...DEFAULT_CHANNEL_FILTER])}
            unassignedOnly={unassignedOnly}
            onUnassignedOnlyChange={setUnassignedOnly}
            onClearStatuses={() => setStatusFilter([...DEFAULT_STATUS_FILTER])}
            onClearPriorities={() => setPriorityFilter([])}
            onClearAll={clearAllFilters}
            showResetToDefault={false}
            filtersAtDefault={filtersAtDefault}
          />

          {viewHasOverrides && (
            <button
              type="button"
              className="support-page__view-clear"
              onClick={clearAllFilters}
              title="検索・#タグ・フィルタ・並び順を既定（入電対応・未完了）に戻す"
            >
              クリア
            </button>
          )}

          <SortBar
            embedded
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortKeyChange={setSortKey}
            onSortOrderChange={setSortOrder}
          />

          {!commandBarExpanded ? (
            <input
              className="support-page__search-input support-page__search-input--inline"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onCompositionStart={() => setIsSearchComposing(true)}
              onCompositionEnd={(e) => { setIsSearchComposing(false); setSearchQ(e.currentTarget.value); }}
              placeholder="検索…"
              title="タイトル・会社名・代理店名・シリアル・担当者名など（表記ゆらぎ・読み対応）"
            />
          ) : (
            <button type="button" className="support-page__command-collapse" onClick={() => toggleCommandBar(false)} title="保存ビュー・タグ検索をたたむ">
              ▴
            </button>
          )}
        </div>

        {commandBarExpanded && (
        <div className="support-page__command-row support-page__command-row--search">
          <input
            className="support-page__search-input"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onCompositionStart={() => setIsSearchComposing(true)}
            onCompositionEnd={(e) => { setIsSearchComposing(false); setSearchQ(e.currentTarget.value); }}
            placeholder="タイトル・会社名・代理店名・シリアルなど（表記ゆらぎ・読み対応）"
          />
          <input
            className="support-page__search-input support-page__search-input--tag"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="#タグ"
            title="タグ完全一致で絞り込み"
          />
          <SavedViews
            current={{ channelFilter, searchQ, tagFilter, sortKey, sortOrder, statusFilter, priorityFilter }}
            onApply={(v) => {
              setChannelFilter(v.channelFilter || []);
              setSearchQ(v.searchQ || "");
              setTagFilter(v.tagFilter || "");
              setSortKey(v.sortKey as TicketSortKey);
              setSortOrder(v.sortOrder as TicketSortOrder);
              setStatusFilter(v.statusFilter || []);
              setPriorityFilter(v.priorityFilter || []);
            }}
          />
        </div>
        )}
      </div>

      {mergeUndo && (
        <div className="support-undo-toast support-undo-toast--merge" role="status">
          <span>🔗 {mergeUndo.count} 件を {mergeUndo.primary} へ統合しました</span>
          <span className="support-undo-toast__actions">
            <button type="button" onClick={() => void undoMerge()} disabled={mergeUndoBusy || !mergeUndo.eventIds.length}>
              {mergeUndoBusy ? "取り消し中…" : "元に戻す"}
            </button>
            <button type="button" className="support-undo-toast__close" onClick={() => setMergeUndo(null)} aria-label="閉じる">×</button>
          </span>
        </div>
      )}

      {assignUndo && (
        <div className="support-undo-toast" role="status">
          <span>
            {assignUndo.ticket.ticket_number} を自分の担当にしました
            {assignUndo.prevStatus === "new" ? "（対応中へ変更）" : ""}
          </span>
          <button type="button" onClick={() => void undoAssign()}>取り消す</button>
        </div>
      )}

      {liveBanner && (
        <div style={{ padding: "0.6rem 0.9rem", background: "var(--danger-light)", color: "var(--danger,#e53935)", border: "1px solid var(--danger,#e53935)", borderRadius: "6px", marginBottom: "0.75rem", fontSize: "0.9rem", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{liveBanner}</span>
          <button onClick={() => setLiveBanner(null)} style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: "1rem" }}>×</button>
        </div>
      )}

      {mergeSuggestNewTicket && mergeSuggestCandidates.length > 0 && (
        <MergeSuggestBanner
          newTicket={mergeSuggestNewTicket}
          candidates={mergeSuggestCandidates}
          primaryId={mergeSuggestPrimaryId}
          canMerge={canDelete}
          onPrimaryChange={setMergeSuggestPrimaryId}
          onMerge={doMergeSuggest}
          onDismiss={clearMergeSuggest}
        />
      )}

      {showRegular && <RegularTicketForm onSubmit={createRegular} onCancel={() => setShowRegular(false)} />}
      {showUrgent && (
        <UrgentForm
          key={urgentInitialType}
          initialTroubleType={urgentInitialType}
          onSubmit={createUrgent}
          onCancel={() => setShowUrgent(false)}
        />
      )}
      {showFollowup && <FollowupForm onSubmit={createFollowup} onCancel={() => setShowFollowup(false)} />}

      {error && (
        <div style={{ padding: "0.6rem 0.9rem", background: "var(--danger-light)", color: "var(--danger,#e53935)", borderRadius: "6px", marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {error}
        </div>
      )}

      {/* 一覧カード幅は viewport 依存の 1fr だとウィンドウ幅で毎回変わっていた（指摘対応）。
          一覧側は固定幅にし、詳細側だけ残り幅に伸びるようにする。 */}
      <div
        className={"support-page__workspace"
          + (listCollapsed ? " support-page__workspace--list-collapsed" : "")
          + (railOpen ? " support-page__workspace--rail" : "")
          + (selected ? " support-page__workspace--has-selection" : "")}
      >
        {/* チケット一覧 */}
        <div className={"support-ticket-list-col" + (listCollapsed ? " support-ticket-list-col--collapsed" : "")}>
          {listCollapsed && (
            <div className="support-list-collapsed">
              <button
                type="button"
                className="support-list-collapsed__expand"
                onClick={() => setListCollapsed(false)}
                title="一覧を開く"
                aria-label="一覧を開く"
              >
                ⟩
              </button>
              <span className="support-list-collapsed__count" title="表示中の件数">{visibleTickets.length}</span>
              {listSummary.over > 0 && (
                <span className="support-list-collapsed__over" title="SLA 超過">{listSummary.over}</span>
              )}
            </div>
          )}
          {/* 一括操作ツールバー（write 以上・選択時に表示） */}
          {canWrite && selectedIds.size > 0 && (
            <div className="support-ticket-list-bulk">
              <div className="support-ticket-list-bulk__actions">
                <span className="support-ticket-list-bulk__count">{selectedIds.size} 件を選択中</span>
                <button type="button" className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--junk" onClick={bulkJunk}>
                  🚫 迷惑としてクローズ
                </button>
                {canDelete && (
                  <>
                <button type="button" className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--danger" onClick={bulkDelete}>
                  🗑 選択を削除
                </button>
                <button
                  type="button"
                  className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--merge"
                  onClick={() => {
                    const minId = Math.min(...Array.from(selectedIds));
                    setMergePrimaryId(minId);
                    setMergeOpen((v) => !v);
                  }}
                  disabled={selectedIds.size < 2}
                  title={selectedIds.size < 2 ? "マージは2件以上選択してください" : "選択したチケットを1件に統合"}
                >
                  🔗 選択をマージ
                </button>
                  </>
                )}
                <button
                  type="button"
                  className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--muted"
                  onClick={() => { setSelectedIds(new Set()); setMergeOpen(false); setMergePrimaryId(null); }}
                >
                  選択解除
                </button>
              </div>
              {canDelete && mergeOpen && selectedIds.size >= 2 && (
                <div className="support-ticket-list-bulk__merge-panel">
                  <div className="support-ticket-list-bulk__merge-title">
                    存続させるチケット（他はここへ統合されます）
                  </div>
                  <div className="support-ticket-list-bulk__merge-options">
                    {tickets.filter((t) => selectedIds.has(t.id)).map((t) => (
                      <label key={t.id} className="support-ticket-list-bulk__merge-option">
                        <input type="radio" name="merge-primary" checked={mergePrimaryId === t.id} onChange={() => setMergePrimaryId(t.id)} />
                        <span className="mono">{t.ticket_number}</span>
                        <span className="support-ticket-list-bulk__merge-label">{ticketDisplayTitle(t)}</span>
                      </label>
                    ))}
                  </div>
                  <div className="support-ticket-list-bulk__merge-actions">
                    <button type="button" className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--merge" onClick={doMerge} disabled={mergePrimaryId == null}>
                      統合を実行（{selectedIds.size - 1} 件を統合）
                    </button>
                    <button type="button" className="support-ticket-list-bulk__btn support-ticket-list-bulk__btn--muted" onClick={() => setMergeOpen(false)}>
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* いま危ないものを常時表示。数字はクリックでその条件に切り替わる（改善 D-3） */}
          <div className="support-list-summary">
            <button
              type="button"
              className={"support-list-summary__item support-list-summary__item--over" + (sortKey === "sla_urgency" ? " support-list-summary__item--active" : "")}
              onClick={() => { setSortKey("sla_urgency"); setSortOrder("asc"); }}
              title="SLA 逼迫順に並べ替える"
            >
              超過 {listSummary.over}
            </button>
            <span className="support-list-summary__sep">・</span>
            <button
              type="button"
              className={"support-list-summary__item" + (unassignedOnly ? " support-list-summary__item--active" : "")}
              onClick={() => setUnassignedOnly((v) => !v)}
              title="未割当だけに絞る"
            >
              未割当 {listSummary.unassigned}
            </button>
            <span className="support-list-summary__sep">・</span>
            <button
              type="button"
              className={"support-list-summary__item" + (statusFilter.length === 1 && statusFilter[0] === "in_progress" ? " support-list-summary__item--active" : "")}
              onClick={() => setStatusFilter((prev) => (prev.length === 1 && prev[0] === "in_progress" ? [...DEFAULT_STATUS_FILTER] : ["in_progress"]))}
              title="対応中だけに絞る"
            >
              対応中 {listSummary.inProgress}
            </button>
            <button
              type="button"
              className="support-list-summary__collapse"
              onClick={() => setListCollapsed(true)}
              title="一覧を畳んで会話に集中する"
              aria-label="一覧を畳む"
            >
              ⟨
            </button>
          </div>
          <div className="support-ticket-list-scroll">
          {loading ? (
            <div style={{ color: "var(--text-secondary,#5f635f)" }}>読み込み中…</div>
          ) : visibleTickets.length === 0 ? (
            <div style={{ color: "var(--text-secondary,#5f635f)", padding: "2rem 0", textAlign: "center" }}>
              {unassignedOnly ? "未割当のチケットはありません"
                : followupOnly ? "後日対応のチケットはありません"
                : "このチャネルのチケットはありません"}
            </div>
          ) : (
            <div className="support-ticket-list">
              {urgentPinned.length > 0 && (
                <section className="support-ticket-pinned">
                  <h4 className="support-ticket-pinned__head">
                    要対応（未割当・SLA超過） {urgentPinned.length}件
                  </h4>
                  {urgentPinned.map((t) => (
                    <TicketCard
                      key={`pinned-${t.id}`}
                      t={t}
                      now={now}
                      isSelected={selected?.id === t.id}
                      canWrite={canWrite}
                      checked={selectedIds.has(t.id)}
                      showChannel={channelFilter.length !== 1}
                      showAssignSelf={canWrite && !t.assigned_to}
                      onToggleCheck={(on) => toggleSelectedId(t.id, on)}
                      onOpen={() => openTicket(t)}
                      onAssignSelf={() => void assignSelf(t)}
                    />
                  ))}
                </section>
              )}
              {visibleTickets.map((t) => (
                <TicketCard
                  key={t.id}
                  t={t}
                  now={now}
                  isSelected={selected?.id === t.id}
                  canWrite={canWrite}
                  checked={selectedIds.has(t.id)}
                  showChannel={channelFilter.length !== 1}
                  showAssignSelf={canWrite && !t.assigned_to}
                  onToggleCheck={(on) => toggleSelectedId(t.id, on)}
                  onOpen={() => openTicket(t)}
                  onAssignSelf={() => void assignSelf(t)}
                />
              ))}
            </div>
          )}
          </div>
        </div>

        {/* 詳細 + チャット（Phase 2: 固定ヘッダー + サイドバー / 会話 2 カラム） */}
        <div className="support-detail-col">
          {!selected ? (
            <div style={{ color: "var(--text-secondary,#5f635f)", padding: "3rem 0", textAlign: "center" }}>
              チケットを選択してください
            </div>
          ) : (
            <TicketDetailPanel
              ticket={selected}
              messages={messages}
              emailLog={emailLog}
              transitions={transitions}
              ticketViewers={ticketViewers}
              moreActionsOpen={moreActionsOpen}
              moreActionsRef={moreActionsRef}
              aiMenuOpen={aiMenuOpen}
              aiMenuRef={aiMenuRef}
              msgEndRef={msgEndRef}
              reply={reply}
              replyToCustomer={replyToCustomer}
              mentionUsers={mentionUsers}
              currentAuthorEmail={currentAuthorEmail}
              currentAuthorRole={currentAuthorRole}
              aiBusy={aiBusy}
              aiSummary={aiSummary}
              sending={sending}
              canWrite={canWrite}
              canEditChannel={canEditChannelFor(selected)}
              onGoToDevice={goToDevice}
              onStatusChange={(s) => updateStatus(selected, s)}
              onAssigneeChange={(email) => saveAssignee(selected, email)}
              onChannelChange={(ch) => saveChannel(selected, ch)}
              onTitleChange={(title) => saveTitle(selected, title)}
              onMoreActionsToggle={() => setMoreActionsOpen((v) => !v)}
              onTriage={() => { setShowTriage(true); setMoreActionsOpen(false); }}
              onCorrection={() => { void sendCorrection(selected); setMoreActionsOpen(false); }}
              onJunkClose={canWrite ? () => { void junkCloseTicket(selected); } : undefined}
              onSaveContacts={(fields) => saveContacts(selected, fields)}
              onScheduleSaved={handleScheduleSaved}
              onTasksChanged={handleTasksChanged}
              onAssetChanged={handleAssetChanged}
              onReplyChange={setReply}
              onReplyToCustomerChange={setReplyToCustomer}
              onAiMenuToggle={() => setAiMenuOpen((v) => !v)}
              onAiDraft={() => void aiDraft()}
              onAiSummarize={() => void aiSummarize()}
              onAiFaqSuggest={() => void aiFaqSuggest()}
              faqSuggestEnabled={selected.status === "resolved" || selected.status === "closed"}
              onKbOpen={() => { setKbOpen(true); setAiMenuOpen(false); }}
              onClearAiSummary={() => setAiSummary("")}
              railOpen={railOpen}
              onRailToggle={() => setRailOpen((v) => !v)}
              onAssignSelf={canWrite && !selected.assigned_to ? () => void assignSelf(selected) : undefined}
              onBackToList={() => setSelected(null)}
              onEditContacts={() => setRailOpen(true)}
              draftSavedAt={draftSavedAt}
              onSend={sendReply}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ===============================
// 通常起票フォーム（チャネル・優先度を選べる手動起票）
function RegularTicketForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (f: {
    channel: string;
    title: string;
    description: string;
    category: string;
    priority: string;
    unit_serial: string;
    device_mac: string;
    product: string;
    company: string;
    contact_name: string;
    contact_emails: string[];
    contact_phones: string[];
  }) => void;
  onCancel: () => void;
}) {
  const categoryHints = useMemo(() => Object.keys(CATEGORY_LABEL), []);
  const [f, setF] = useState({
    channel: "partner",
    title: "",
    description: "",
    category: "",
    priority: "normal",
    unit_serial: "",
    device_mac: "",
    product: "",
    company: "",
    contact_name: "",
  });
  const [emails, setEmails] = useState<string[]>([""]);
  const [phones, setPhones] = useState<string[]>([""]);
  const [serialOpts, setSerialOpts] = useState<string[]>([]);
  const timer = useRef<number | null>(null);
  const inp: React.CSSProperties = { padding: "0.5rem 0.7rem", borderRadius: "6px", border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  useEffect(() => {
    const q = f.unit_serial.trim();
    if (timer.current) window.clearTimeout(timer.current);
    if (q.length < 2) { setSerialOpts([]); return; }
    timer.current = window.setTimeout(async () => {
      try {
        const r = await fetch(`/api/campaign/serial_suggest?q=${encodeURIComponent(q)}&limit=10`);
        const d = await r.json();
        setSerialOpts(Array.isArray(d?.serials) ? d.serials : []);
      } catch { setSerialOpts([]); }
    }, 200);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [f.unit_serial]);

  const lookup = async () => {
    const s = f.unit_serial.trim();
    const m = f.device_mac.trim();
    if (!s && !m) return;
    try {
      const qs = new URLSearchParams();
      if (s) qs.set("serial", s);
      if (m) qs.set("mac", m);
      const r = await fetch(`/api/campaign/customer_lookup?${qs.toString()}`);
      const d = await r.json();
      setF((prev) => ({
        ...prev,
        device_mac: prev.device_mac || d.device_mac || "",
        company: prev.company || d.company_name || "",
        contact_name: prev.contact_name || d.person_name || "",
        product: prev.product || d.product || "",
      }));
      setEmails((prev) => (prev.filter(Boolean).length ? prev : (d.email ? [d.email] : [""])));
      setPhones((prev) => (prev.filter(Boolean).length ? prev : ([d.phone_mobile, d.phone_office].filter(Boolean) as string[])));
    } catch { /* noop */ }
  };

  const cleanEmails = emails.map((s) => s.trim()).filter(Boolean);
  const cleanPhones = phones.map((s) => s.trim()).filter(Boolean);
  const canSubmit = !!f.title.trim();

  return (
    <div style={{ border: "1.5px solid var(--primary,#358b39)", borderRadius: "10px", padding: "1rem 1.25rem", marginBottom: "1rem", background: "#f8fbf8" }}>
      <div style={{ fontWeight: 800, color: "var(--primary,#358b39)", marginBottom: "0.6rem" }}>🎫 通常起票（手動）</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem" }}>
          チャネル
          <select style={inp} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
            {MANUAL_CHANNELS.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem" }}>
          優先度
          <select style={inp} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            <option value="normal">通常</option>
            <option value="high">高</option>
            <option value="urgent">緊急</option>
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem" }}>
          種別
          <input style={inp} list="regular-ticket-categories" placeholder="usage / repair / 設置トラブル …" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <datalist id="regular-ticket-categories">
            {categoryHints.map((k) => <option key={k} value={k}>{CATEGORY_LABEL[k]}</option>)}
          </datalist>
        </label>
      </div>
      <input style={{ ...inp, marginBottom: "0.6rem" }} placeholder="件名（必須）" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <textarea style={{ ...inp, marginBottom: "0.6rem" }} rows={3} placeholder="内容（任意）" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.4rem" }}>
        <input style={{ ...inp, fontFamily: "'JetBrains Mono',monospace" }} placeholder="シリアル（任意・2文字で予測変換）" list="regular-serial-list" autoComplete="off" value={f.unit_serial} onChange={(e) => setF({ ...f, unit_serial: e.target.value.toUpperCase() })} onBlur={lookup} />
        <datalist id="regular-serial-list">{serialOpts.map((s) => <option key={s} value={s} />)}</datalist>
        <input style={{ ...inp, fontFamily: "'JetBrains Mono',monospace" }} placeholder="MAC（任意）" value={f.device_mac} onChange={(e) => setF({ ...f, device_mac: e.target.value })} onBlur={lookup} />
        <input style={inp} placeholder="機種（任意）" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} />
        <input style={inp} placeholder="会社名（任意）" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
        <input style={inp} placeholder="担当者名（任意）" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
      </div>
      <button type="button" onClick={lookup} style={{ padding: "0.3rem 0.7rem", borderRadius: 6, border: "1.5px solid var(--primary,#358b39)", background: "transparent", color: "var(--primary,#358b39)", fontSize: "0.8rem", cursor: "pointer", marginBottom: "0.6rem" }}>
        シリアル/MAC から連絡先を取得
      </button>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <ContactListInput label="連絡先メール（任意）" values={emails} onChange={setEmails} placeholder="you@example.com" type="email" />
        <ContactListInput label="連絡先電話（任意）" values={phones} onChange={setPhones} placeholder="090-1234-5678" />
      </div>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button type="button" onClick={onCancel} style={{ padding: "0.5rem 1rem", borderRadius: "6px", border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer" }}>取消</button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => canSubmit && onSubmit({ ...f, contact_emails: cleanEmails, contact_phones: cleanPhones })}
          style={{ padding: "0.5rem 1.25rem", borderRadius: "6px", border: "none", background: canSubmit ? "var(--primary,#358b39)" : "var(--text-disabled,#aeb2ae)", color: "#fff", fontWeight: 700, cursor: canSubmit ? "pointer" : "default" }}
        >
          起票する
        </button>
      </div>
    </div>
  );
}

// ===============================
// 後日対応 起票フォーム（手動・コンソール）。連絡先はシリアル/MACからDB自動取得。
function FollowupForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (f: {
    unit_serial: string; device_mac: string; product: string; company: string;
    contact_name: string; contact_emails: string[]; contact_phones: string[];
    items: { type: string; detail?: string; qty?: number }[]; revisit_period: string;
    customer_explained: boolean; notes: string;
  }) => void;
  onCancel: () => void;
}) {
  // 後日対応の品目は **BE の共通部品マスタ（正）を API で取得**する。
  // ここに定数を書くと現場アプリ(/service)と選択肢がズレる（2026-07-17 に実際に発生）。
  // 正: ops/app/utils/parts_master.py → GET /api/support/parts-master
  const [parts, setParts] = useState<PartMaster[]>([]);
  const FREE = useMemo(() => new Set(parts.filter((p) => p.free_detail).map((p) => p.followup_label)), [parts]);
  const [f, setF] = useState({ unit_serial: "", device_mac: "", product: "EC-700", company: "", contact_name: "", revisit_period: "", notes: "", customer_explained: false });
  // 連絡先は複数可。メール必須・電話任意（従来は callback_contact 1欄に混在していた）
  const [emails, setEmails] = useState<string[]>([""]);
  const [phones, setPhones] = useState<string[]>([""]);
  const [items, setItems] = useState<{ type: string; on: boolean; detail: string; qty: number }[]>([]);

  useEffect(() => {
    api<{ parts: PartMaster[] }>(`/support/parts-master`)
      .then((d) => {
        const ps = d.parts || [];
        setParts(ps);
        // 「その他」は自由記述として末尾に固定で1つ用意（BE マスタは実部品のみ）
        setItems([...ps.map((p) => ({ type: p.followup_label, on: false, detail: "", qty: 1 })),
                  { type: "その他", on: false, detail: "", qty: 1 }]);
      })
      .catch(() => setItems([{ type: "その他", on: false, detail: "", qty: 1 }]));
  }, []);
  const [serialOpts, setSerialOpts] = useState<string[]>([]);
  const [registered, setRegistered] = useState<boolean | null>(null);
  const timer = useRef<number | null>(null);
  const inp: React.CSSProperties = { padding: "0.5rem 0.7rem", borderRadius: "6px", border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  useEffect(() => {
    const q = f.unit_serial.trim();
    if (timer.current) window.clearTimeout(timer.current);
    if (q.length < 2) { setSerialOpts([]); return; }
    timer.current = window.setTimeout(async () => {
      try {
        const r = await fetch(`/api/campaign/serial_suggest?q=${encodeURIComponent(q)}&limit=10`);
        const d = await r.json();
        setSerialOpts(Array.isArray(d?.serials) ? d.serials : []);
      } catch { setSerialOpts([]); }
    }, 200);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [f.unit_serial]);

  const lookup = async () => {
    const s = f.unit_serial.trim(); const m = f.device_mac.trim();
    if (!s && !m) return;
    try {
      const qs = new URLSearchParams(); if (s) qs.set("serial", s); if (m) qs.set("mac", m);
      const r = await fetch(`/api/campaign/customer_lookup?${qs.toString()}`);
      const d = await r.json();
      setRegistered(!!d.registered);
      setF((prev) => ({
        ...prev,
        device_mac: prev.device_mac || d.device_mac || "",
        company: prev.company || d.company_name || "",
        contact_name: prev.contact_name || d.person_name || "",
      }));
      // DB由来の連絡先は、未入力のときだけ初期値として入れる（手入力を上書きしない）
      setEmails((prev) => (prev.filter(Boolean).length ? prev : (d.email ? [d.email] : [""])));
      setPhones((prev) => (prev.filter(Boolean).length ? prev : ([d.phone_mobile, d.phone_office].filter(Boolean) as string[])));
    } catch { /* noop */ }
  };

  const cleanEmails = emails.map((s) => s.trim()).filter(Boolean);
  const cleanPhones = phones.map((s) => s.trim()).filter(Boolean);
  // メールは1件以上必須（BE 側でも検証）
  const canSubmit = !!((f.unit_serial.trim() || f.device_mac.trim()) && items.some((i) => i.on) && cleanEmails.length > 0);
  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      unit_serial: f.unit_serial.trim(), device_mac: f.device_mac.trim(), product: f.product,
      company: f.company, contact_name: f.contact_name,
      contact_emails: cleanEmails, contact_phones: cleanPhones,
      items: items.filter((i) => i.on).map((i) => ({ type: i.type, detail: i.detail.trim() || undefined, qty: i.qty })),
      revisit_period: f.revisit_period, customer_explained: f.customer_explained, notes: f.notes,
    });
  };

  return (
    <div style={{ border: "1.5px solid var(--primary,#358b39)", borderRadius: "10px", padding: "1rem 1.25rem", marginBottom: "1rem", background: "#f2f9f2" }}>
      <div style={{ fontWeight: 800, color: "var(--primary,#358b39)", marginBottom: "0.6rem" }}>🔧 後日対応 起票（手動）</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.4rem" }}>
        <input style={{ ...inp, fontFamily: "'JetBrains Mono',monospace" }} placeholder="シリアル番号（2文字で予測変換）" list="fu-serial-list" autoComplete="off" value={f.unit_serial} onChange={(e) => setF({ ...f, unit_serial: e.target.value.toUpperCase() })} onBlur={lookup} />
        <datalist id="fu-serial-list">{serialOpts.map((s) => <option key={s} value={s} />)}</datalist>
        <input style={{ ...inp, fontFamily: "'JetBrains Mono',monospace" }} placeholder="MAC アドレス（シリアルが無い場合）" value={f.device_mac} onChange={(e) => setF({ ...f, device_mac: e.target.value })} onBlur={lookup} />
      </div>
      <div style={{ marginBottom: "0.6rem" }}>
        <button onClick={lookup} style={{ padding: "0.3rem 0.7rem", borderRadius: 6, border: "1.5px solid var(--primary,#358b39)", background: "transparent", color: "var(--primary,#358b39)", fontSize: "0.8rem", cursor: "pointer" }}>連絡先をDBから取得</button>
        {registered === false && <span style={{ marginLeft: 8, fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)" }}>未登録機（手入力で起票）</span>}
      </div>
      <div style={{ fontSize: "0.8rem", color: "var(--text-secondary,#5f635f)", margin: "0.2rem 0" }}>対応/交換項目（数量）</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {items.map((it, idx) => (
          <div key={it.type} style={{ display: "flex", alignItems: "center", gap: 4, border: "1px solid var(--border,#e0e4e0)", borderRadius: 6, padding: "3px 6px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
              <input type="checkbox" checked={it.on} onChange={(e) => setItems((a) => a.map((x, i) => (i === idx ? { ...x, on: e.target.checked } : x)))} />{it.type}
            </label>
            {it.on && FREE.has(it.type) && <input value={it.detail} placeholder={it.type === "ポート交換" ? "AC/USB/DC" : "内容"} onChange={(e) => setItems((a) => a.map((x, i) => (i === idx ? { ...x, detail: e.target.value } : x)))} style={{ width: 90, padding: "3px 5px", borderRadius: 4, border: "1px solid var(--border,#e0e4e0)" }} />}
            {it.on && <input type="number" min={1} value={it.qty} onChange={(e) => setItems((a) => a.map((x, i) => (i === idx ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) } : x)))} style={{ width: 44, padding: "3px 5px", borderRadius: 4, border: "1px solid var(--border,#e0e4e0)" }} />}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <input style={inp} placeholder="再訪希望時期（例:部品入荷後）" value={f.revisit_period} onChange={(e) => setF({ ...f, revisit_period: e.target.value })} />
        <input style={inp} placeholder="機種（対象製品）" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} />
        <input style={inp} placeholder="会社名" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
        <input style={inp} placeholder="担当者名" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
      </div>
      {/* 連絡先: メール（必須・複数可）／電話（任意・複数可）。従来は1欄に混在入力していた */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.4rem" }}>
        <ContactListInput label="連絡先メール" required values={emails} onChange={setEmails}
                          placeholder="you@example.com" type="email" note="この全員に連絡が届きます" />
        <ContactListInput label="連絡先電話" values={phones} onChange={setPhones} placeholder="090-1234-5678" />
      </div>
      <textarea style={{ ...inp, marginBottom: "0.6rem" }} rows={2} placeholder="状況/メモ" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.6rem", fontSize: "0.85rem" }}>
        <input type="checkbox" checked={f.customer_explained} onChange={(e) => setF({ ...f, customer_explained: e.target.checked })} />お客様へ説明・了承済み
      </label>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "0.5rem 1rem", borderRadius: 6, border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer" }}>取消</button>
        <button onClick={submit} disabled={!canSubmit} style={{ padding: "0.5rem 1.25rem", borderRadius: 6, border: "none", background: canSubmit ? "var(--primary,#358b39)" : "var(--text-disabled,#aeb2ae)", color: "#fff", fontWeight: 700, cursor: canSubmit ? "pointer" : "default" }}>後日対応を起票</button>
      </div>
    </div>
  );
}

// 緊急起票フォーム（代理店の設置・急トラブルを社内が代理起票／将来は代理店側から）
// ===============================
function UrgentForm({
  onSubmit,
  onCancel,
  initialTroubleType = "設置トラブル",
}: {
  onSubmit: (f: { title: string; trouble_type: string; description: string; partner_code: string; case_no: string; product: string; unit_serial: string; device_mac: string; contact_name: string; contact_affiliation: string; install_date: string; company: string; contact_emails: string[]; contact_phones: string[] }, files: File[]) => void;
  onCancel: () => void;
  initialTroubleType?: string;
}) {
  const [f, setF] = useState({ title: "", description: "", partner_code: "", case_no: "", product: "", unit_serial: "", device_mac: "", contact_name: "", contact_affiliation: "", install_date: "", company: "" });
  // 連絡先は複数可。メール必須・電話任意（従来は callback_contact 1欄に混在していた）
  const [emails, setEmails] = useState<string[]>([""]);
  const [phones, setPhones] = useState<string[]>([""]);
  const [troubleType, setTroubleType] = useState(initialTroubleType);
  const emg = troubleType === "緊急トラブル対応";
  const [files, setFiles] = useState<File[]>([]);
  const [serialOpts, setSerialOpts] = useState<string[]>([]);
  const serialTimer = useRef<number | null>(null);
  const upd = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  // 機番 予測変換（デバウンス）。dash は installApiAuthFetch で JWT 自動付与。
  useEffect(() => {
    const q = f.unit_serial.trim();
    if (serialTimer.current) window.clearTimeout(serialTimer.current);
    if (q.length < 2) { setSerialOpts([]); return; }
    serialTimer.current = window.setTimeout(async () => {
      try {
        const res = await fetch(`/api/campaign/serial_suggest?q=${encodeURIComponent(q)}&limit=10`);
        const data = await res.json();
        setSerialOpts(Array.isArray(data?.serials) ? data.serials : []);
      } catch { setSerialOpts([]); }
    }, 200);
    return () => { if (serialTimer.current) window.clearTimeout(serialTimer.current); };
  }, [f.unit_serial]);
  const inputStyle: React.CSSProperties = { padding: "0.5rem 0.7rem", borderRadius: "6px", border: "1.5px solid var(--border,#e0e4e0)", fontFamily: "inherit", width: "100%" };
  // 件名必須＋対象機の特定のためシリアル or MAC のどちらか必須（バックエンドの 422 と一致）
  const cleanEmails = emails.map((x) => x.trim()).filter(Boolean);
  const cleanPhones = phones.map((x) => x.trim()).filter(Boolean);
  // 件名＋機体特定＋連絡先メール1件以上（メール必須・BE 側でも検証）
  const canSubmit = !!(f.title.trim() && (f.unit_serial.trim() || f.device_mac.trim()) && cleanEmails.length > 0);
  const monoStyle: React.CSSProperties = { ...inputStyle, fontFamily: "'JetBrains Mono',monospace" };
  const secStyle: React.CSSProperties = { gridColumn: "1 / -1", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--primary,#358b39)", fontWeight: 700, borderTop: "1.5px solid var(--border,#e0e4e0)", paddingTop: "0.5rem", marginTop: "0.2rem" };
  return (
    <div style={{ border: "1.5px solid var(--danger,#e53935)", borderRadius: "10px", padding: "1rem 1.25rem", marginBottom: "1rem", background: "var(--danger-light)" }}>
      <div style={{ fontWeight: 800, color: "var(--danger,#e53935)", marginBottom: "0.5rem" }}>{troubleType === "緊急トラブル対応" ? "🆘" : "🚨"} 代理店 緊急起票（{troubleType}）</div>
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {["設置トラブル", "緊急トラブル対応"].map((t) => (
          <button key={t} onClick={() => setTroubleType(t)}
            style={{ flex: 1, padding: "0.4rem 0.6rem", borderRadius: "6px", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer",
              border: "1.5px solid " + (troubleType === t ? "var(--danger,#e53935)" : "var(--border,#e0e4e0)"),
              background: troubleType === t ? "var(--danger,#e53935)" : "transparent",
              color: troubleType === t ? "#fff" : "var(--text)" }}>
            {t === "緊急トラブル対応" ? "🆘 " : "🚨 "}{t}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        {/* 島1: 基礎情報 */}
        <div style={{ ...secStyle, borderTop: "none", paddingTop: 0, marginTop: 0 }}>基礎情報</div>
        <input
          style={monoStyle}
          placeholder="シリアル番号（必須・2文字以上で予測変換）"
          list="dash-urgent-serial-list"
          autoComplete="off"
          value={f.unit_serial}
          onChange={(e) => setF({ ...f, unit_serial: e.target.value.toUpperCase() })}
        />
        <datalist id="dash-urgent-serial-list">
          {serialOpts.map((s) => <option key={s} value={s} />)}
        </datalist>
        <input style={monoStyle} placeholder="MAC アドレス（任意・シリアルが無い場合）" autoComplete="off" value={f.device_mac} onChange={upd("device_mac")} />
        <input style={inputStyle} placeholder="機種名（対象製品・任意）" value={f.product} onChange={upd("product")} />
        <input style={inputStyle} placeholder="取引先コード（任意）" value={f.partner_code} onChange={upd("partner_code")} />
        {emg ? (
          <>
            <input style={inputStyle} placeholder="設置日／稼働開始日（任意・例:2026-03-15）" value={f.install_date} onChange={upd("install_date")} />
            <input style={inputStyle} placeholder="お客様名／設置先（任意）" value={f.company} onChange={upd("company")} />
          </>
        ) : (
          <input style={inputStyle} placeholder="案件番号（任意）" value={f.case_no} onChange={upd("case_no")} />
        )}
        {/* 島2: 連絡先情報 */}
        <div style={secStyle}>連絡先情報</div>
        <input style={inputStyle} placeholder="代理店名（任意・所属先／会社・部署）" value={f.contact_affiliation} onChange={upd("contact_affiliation")} />
        <input style={inputStyle} placeholder="連絡担当者名" value={f.contact_name} onChange={upd("contact_name")} />
        {/* 連絡先: メール（必須・複数可）／電話（任意・複数可）。従来は1欄に混在入力していた */}
        <ContactListInput label="連絡先メール" required values={emails} onChange={setEmails}
                          placeholder="you@example.com" type="email" note="この全員に連絡が届きます" />
        <ContactListInput label="折り返し連絡先（電話）" values={phones} onChange={setPhones}
                          placeholder="090-1234-5678" />
      </div>
      {/* 島3: 事象 */}
      <div style={{ ...secStyle, gridColumn: "auto", marginBottom: "0.4rem" }}>事象</div>
      <input style={{ ...inputStyle, marginBottom: "0.6rem" }} placeholder="件名（必須・何が起きているか）" value={f.title} onChange={upd("title")} />
      <textarea style={{ ...inputStyle, marginBottom: "0.6rem" }} rows={3} placeholder={emg ? "症状・発生日時・危険度（発煙/発熱等）など（任意）" : "状況（設置場所・症状・緊急度など・任意）"} value={f.description} onChange={upd("description")} />
      <label style={{ fontSize: "0.8rem", color: "var(--text-secondary,#5f635f)" }}>写真・動画（任意・複数可）</label>
      <input type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))}
        style={{ ...inputStyle, marginTop: "0.25rem", marginBottom: files.length ? "0.3rem" : "0.6rem" }} />
      {files.length > 0 && <div style={{ fontSize: "0.75rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.6rem" }}>{files.length} 件を添付</div>}
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", alignItems: "center" }}>
        {!canSubmit && (
          <span style={{ marginRight: "auto", fontSize: "0.78rem", color: "var(--text-secondary,#5f635f)" }}>
            件名・連絡先メール（1件以上）と、シリアル または MAC のどちらかが必須です
          </span>
        )}
        <button onClick={onCancel} style={{ padding: "0.5rem 1rem", borderRadius: "6px", border: "1px solid var(--border,#e0e4e0)", background: "transparent", cursor: "pointer" }}>取消</button>
        <button
          onClick={() => canSubmit && onSubmit({ ...f, trouble_type: troubleType, contact_emails: cleanEmails, contact_phones: cleanPhones }, files)}
          disabled={!canSubmit}
          style={{ padding: "0.5rem 1.25rem", borderRadius: "6px", border: "none", background: canSubmit ? "var(--danger,#e53935)" : "var(--text-disabled,#aeb2ae)", color: "#fff", fontWeight: 700, cursor: canSubmit ? "pointer" : "default" }}
        >
          緊急起票して通知
        </button>
      </div>
    </div>
  );
}
